import { execFile } from 'child_process'
import { createCipheriv, createDecipheriv, createHash, randomBytes, randomUUID } from 'crypto'
import { existsSync, readdirSync, realpathSync } from 'fs'
import { chmod, mkdir, open, readFile, rename, rm, stat, writeFile } from 'fs/promises'
import { homedir } from 'os'
import { delimiter, dirname, join } from 'path'
import { promisify } from 'util'
import { getWebUiHome } from '../../config'
import { PROVIDER_ENV_MAP, readConfigYamlForProfile, safeReadFile } from '../config-helpers'
import { getCompatibleCustomProviders } from '../hermes/custom-providers-compat'
import { registerClaudeCodeProxyTarget } from './claude-code/proxy'
import { registerCodexProxyTarget, restoreCodexProxyTarget } from './codex/proxy'
import { compactCodexThread } from './runtime/codex-compact'
import type { ApiMode, CodingAgentImageInput } from './shared/types'
import { PROVIDER_PRESETS } from '../../shared/providers'
import { getModelContextLength, getModelRuntimeCapabilities } from '../hermes/model-context'
import { getProfileDir } from '../hermes/hermes-profile'
import { getSystemPrompt } from '../../lib/llm-prompt'
import { codingAgentRunManager } from './runtime/run-manager'
import { PI_EXTENDED_THINKING_LEVEL_MAP, piModelSupportsThinking } from './pi/thinking'
import { getSession, updateSession, type HermesSessionRow } from '../../db/hermes/session-store'
import type { SessionState } from '../hermes/run-chat/types'
import { normalizeWindowsCommandPath, windowsCmdShimExecution, windowsCommandNeedsShell, type WindowsCommandExecution } from '../windows-command'
import { assertScopedCodingAgentProviderAllowed } from './shared/provider-policy'

const execFileAsync = promisify(execFile)
const LAUNCH_API_MODES = new Set<ApiMode>(['chat_completions', 'codex_responses', 'anthropic_messages'])
const CODING_AGENT_HOME_DIR = 'coding-agent'
const CODEX_MODEL_CATALOG_FILE = 'codex-model-catalog.json'
const CODEX_CATALOG_BASE_INSTRUCTIONS = 'You are Codex, a coding agent. Be precise, safe, and helpful.'
const DSH_SDK_CORDIS_FILE = 'cordis.yml'
const DSH_SDK_CORDIS_TEMPLATE = `# Unattended DeepSeek Harness coding-agent composition managed by Hermes Web UI.
# stdout is reserved for JSON-RPC; do not add a console logger or terminal UI.

- id: sdk-jsonrpc-server
  name: '@deepseek-ai/dsh-sdk-jsonrpc-server'
  config:
    maxTokensAsSuccess: !!js "process.env.DSH_MAX_TOKENS_AS_SUCCESS === undefined ? true : JSON.parse(process.env.DSH_MAX_TOKENS_AS_SUCCESS)"

- id: llm-deepseek
  name: '@deepseek-ai/dsh-llm-deepseek'
  config:
    thinking: enabled
    reasoningEffort: max

# OpenAI Responses protocol route, selected when a session runs in
# codex_responses apiMode. The endpoint/key travel via env; without a
# DEEPSEEK_BASE_URL the route fails fast with a clear config error.
- id: llm-pi-responses
  name: '@deepseek-ai/dsh-llm-pi-ai'
  config:
    providers:
      openai-responses:
        apiKeyEnv: DEEPSEEK_API_KEY
        api: openai-responses
        baseURL: !!js "process.env.DEEPSEEK_BASE_URL ?? ''"
        models:
          - id: deepseek-v4-pro
            contextWindow: 262144
            maxTokens: 32768
          - id: deepseek-v4-flash
            contextWindow: 262144
            maxTokens: 32768
          - id: deepseek-chat
            contextWindow: 65536
            maxTokens: 8192
          - id: deepseek-reasoner
            contextWindow: 65536
            maxTokens: 8192

- id: subprocess
  name: '@deepseek-ai/dsh-subprocess-local'

- id: bash
  name: '@deepseek-ai/dsh-bash-local'
  config:
    cwd: !!js process.env.DSH_CWD ?? process.cwd()
    timeoutMs: 60000

- id: agent-spine
  name: '@deepseek-ai/dsh-agent-spine-demo'
  config:
    persona: !!js process.env.DSH_SYSTEM_PROMPT ?? 'You are a coding agent.'
    workspaceContext: false
    skills:
      enabled: false
    toolBash:
      enableRunInBackground: false
    toolJobs: false

- id: sessions
  name: '@deepseek-ai/dsh-session-persistence-jsonl'
  config:
    root: !!js process.env.DSH_SESSION_ROOT ?? './.sessions'
    compression: zstd

- id: session-checkpoints
  name: '@deepseek-ai/dsh-session-checkpoint-policy'

- id: subagent
  name: '@deepseek-ai/dsh-subagent'

- id: subagent-spawn-in-process
  name: '@deepseek-ai/dsh-subagent-spawn-in-process'
  config:
    providerName: spawn

- id: tool-subagent
  name: '@deepseek-ai/dsh-tool-subagent'
  config:
    provider: spawn
    toolName: subagent
    enableRunInBackground: false

- id: tool-todo
  name: '@deepseek-ai/dsh-tool-todo'
  config:
    allowParallelInProgress: true

- id: fs-local
  name: '@deepseek-ai/dsh-fs-local'
  config:
    cwd: !!js process.env.DSH_CWD ?? process.cwd()

- id: fs-observation-policy
  name: '@deepseek-ai/dsh-fs-observation-policy'

- id: tool-fs
  name: '@deepseek-ai/dsh-tool-fs'

- id: token-meter
  name: '@deepseek-ai/dsh-token-meter'

- id: compaction-basic
  name: '@deepseek-ai/dsh-compaction-basic'
  config:
    thresholdRatio: 0.8
    retainRatio: 0.16
    maxTokens: 8192
    compactionRetries: 1
`
/**
 * Build the SDK cordis composition for a launch's apiMode. The llm-pi-ai
 * OpenAI Responses route is only required by codex_responses sessions, so
 * chat-completions boots stay free of it and never need DEEPSEEK_BASE_URL.
 * The block is delimited by its comment and the next top-level entry; if the
 * template structure ever changes, the safe fallback keeps the full template.
 */
export function buildDshSdkCordisTemplate(apiMode?: string): string {
  if (apiMode === 'codex_responses') return DSH_SDK_CORDIS_TEMPLATE
  const blockStart = DSH_SDK_CORDIS_TEMPLATE.indexOf('# OpenAI Responses protocol route')
  const blockEnd = blockStart < 0 ? -1 : DSH_SDK_CORDIS_TEMPLATE.indexOf('- id: subprocess', blockStart)
  if (blockStart < 0 || blockEnd < 0) return DSH_SDK_CORDIS_TEMPLATE
  return DSH_SDK_CORDIS_TEMPLATE.slice(0, blockStart) + DSH_SDK_CORDIS_TEMPLATE.slice(blockEnd)
}
const DSH_SDK_BIN = 'dsh-jsonrpc-agent'
const DSH_SDK_SRC_BIN = 'packages/examples/jsonrpc-demo/src/bin.ts'
const DSH_SDK_SRC_CONFIG = 'examples/jsonrpc-agent/cordis.yml'
/**
 * Published npm package providing the `dsh-jsonrpc-agent` bin for the full
 * streaming DeepSeek Harness SDK runtime, and the coherent version series to
 * pin it together with every cordis component in {@link DSH_SDK_CORDIS_TEMPLATE}.
 *
 * DSH publishes several series (the `latest` dist-tag still points at an old
 * 0.0.1-rc.x series whose dependencies are not all published). The 0.1.0-rc.7
 * series is the first self-consistent set: every template component plus the
 * runtime bin resolves without ERESOLVE or missing packages. Bump this constant
 * together with new DSH releases and re-run the isolated-prefix install check.
 */
const DSH_SDK_VERSION = '0.1.0-rc.7'
const DSH_SDK_RUNTIME_PACKAGE = '@deepseek-ai/dsh-sdk-jsonrpc-demo'
const DEFAULT_DEEPSEEK_BASE_URL = 'https://api.deepseek.com'
const NODE_ENVIRONMENT_MISSING_CODE = 'node_environment_missing'
const POSIX_LAUNCHER_FILE = 'launch.sh'
const WINDOWS_LAUNCHER_FILE = 'launch.ps1'
const CLAUDE_CODE_SKIP_PERMISSIONS_ARGS = ['--dangerously-skip-permissions']
const CLAUDE_CODE_ROOT_PERMISSION_ARGS = ['--permission-mode', 'auto']
// Claude Code auto-compact is on by default, but Studio never tells it the
// model context window, so it can compact too late for the 20MB proxy body
// limit. Mirror Hermes' 50% compression budget and pass Studio's window.
const CLAUDE_CODE_AUTO_COMPACT_PERCENT = 50
const PI_MCP_ADAPTER_VERSION = '2.24.0'
const PI_MCP_ADAPTER_PACKAGE = `pi-mcp-adapter@${PI_MCP_ADAPTER_VERSION}`
const PI_CODING_AGENT_VERSION = '0.84.1'
const PI_CODING_AGENT_PACKAGE = `@earendil-works/pi-coding-agent@${PI_CODING_AGENT_VERSION}`
const PI_PROVIDER_ID = 'hermes-studio'
const PI_PROXY_TARGET_FILE = 'proxy-target.json'
const PI_DYNAMIC_PROMPT_FILE = 'dynamic-system-prompt.md'
const PI_STUDIO_EXTENSION_FILE = 'hermes-studio-runtime.ts'
const PI_PROXY_TARGET_KEY_FILE = '.pi-proxy-target.key'
const PI_PROXY_TARGET_LEGACY_AAD = Buffer.from('hermes-studio/pi-proxy-target/v1', 'utf8')
// Codex ToolSearch became stable in 0.128; always-defer was removed in 0.142.
const CODEX_TOOL_SEARCH_MIN_VERSION = '0.128.0'
const CODEX_TOOL_SEARCH_ALWAYS_DEFER_REMOVED_VERSION = '0.142.0'
const CODEX_VERSION_CACHE_TTL_MS = 5 * 60 * 1000
const HERMES_MCP_SERVERS: ReadonlyArray<{ name: string; toolset: string }> = [
  { name: 'hermes-studio-api', toolset: 'api' },
  { name: 'hermes-studio-browser', toolset: 'browser' },
  { name: 'hermes-studio-devices', toolset: 'devices' },
  { name: 'hermes-studio-use', toolset: 'use' },
]
const HERMES_MCP_SERVER_NAMES: Set<string> = new Set(HERMES_MCP_SERVERS.map(server => server.name))
const LEGACY_HERMES_MCP_SERVER_NAMES = new Set(['hermes-studio', 'hermes-studio-mcp', 'hermes-web-ui-mcp'])
const LEGACY_HERMES_MCP_COMMANDS = new Set([
  'hermes-lan-peer-mcp',
  'hermes-devices-mcp',
  'hermes-web-ui-mcp',
  'hermes-studio-mcp',
])
const HERMES_MCP_MANAGED_ENV_KEY = 'HERMES_WEB_UI_MANAGED_MCP'
const HERMES_PROMPT_BLOCK_BEGIN = '<!-- BEGIN HERMES WEB UI PROMPT -->'
const HERMES_PROMPT_BLOCK_END = '<!-- END HERMES WEB UI PROMPT -->'

let cachedCodexVersion: { version: string; checkedAt: number } | null = null

interface EncryptedPiProxyApiKey {
  v: 1 | 2
  algorithm: 'aes-256-gcm'
  iv: string
  tag: string
  ciphertext: string
}

function piProxyTargetAad(input: Record<string, unknown>, token: string): Buffer {
  return Buffer.from(JSON.stringify({
    v: 2,
    profile: String(input.profile || ''),
    provider: String(input.provider || ''),
    model: String(input.model || ''),
    baseUrl: String(input.baseUrl || ''),
    apiMode: String(input.apiMode || ''),
    reasoningEffort: String(input.reasoningEffort || ''),
    agentId: String(input.agentId || ''),
    agentSessionId: String(input.agentSessionId || ''),
    chatSessionId: String(input.chatSessionId || ''),
    token,
  }), 'utf8')
}

async function atomicWritePrivateFile(path: string, content: string | Buffer): Promise<void> {
  await mkdir(dirname(path), { recursive: true })
  const temporaryPath = `${path}.${process.pid}.${randomUUID()}.tmp`
  const handle = await open(temporaryPath, 'wx', 0o600)
  try {
    await handle.writeFile(content)
    await handle.sync()
    await handle.close()
  } catch (err) {
    await handle.close().catch(() => {})
    await rm(temporaryPath, { force: true }).catch(() => {})
    throw err
  }
  try {
    await rename(temporaryPath, path)
    await chmod(path, 0o600)
  } catch (err) {
    await rm(temporaryPath, { force: true }).catch(() => {})
    throw err
  }
}

function piProxyTargetKeyPath(): string {
  return join(getWebUiHome(), CODING_AGENT_HOME_DIR, PI_PROXY_TARGET_KEY_FILE)
}

async function readOrCreatePiProxyTargetKey(): Promise<Buffer> {
  const path = piProxyTargetKeyPath()
  await mkdir(dirname(path), { recursive: true })
  try {
    const existing = await readFile(path)
    if (existing.length !== 32) throw new Error(`Invalid Pi proxy target encryption key length: ${existing.length}`)
    await chmod(path, 0o600)
    return existing
  } catch (err: any) {
    if (err?.code !== 'ENOENT') throw err
  }

  const generated = randomBytes(32)
  try {
    await writeFile(path, generated, { mode: 0o600, flag: 'wx' })
    return generated
  } catch (err: any) {
    if (err?.code !== 'EEXIST') throw err
    const existing = await readFile(path)
    if (existing.length !== 32) throw new Error(`Invalid Pi proxy target encryption key length: ${existing.length}`)
    await chmod(path, 0o600)
    return existing
  }
}

async function encryptPiProxyApiKey(
  apiKey: string,
  input: Record<string, unknown>,
  token: string,
): Promise<EncryptedPiProxyApiKey> {
  const key = await readOrCreatePiProxyTargetKey()
  const iv = randomBytes(12)
  const cipher = createCipheriv('aes-256-gcm', key, iv)
  cipher.setAAD(piProxyTargetAad(input, token))
  const ciphertext = Buffer.concat([cipher.update(apiKey, 'utf8'), cipher.final()])
  return {
    v: 2,
    algorithm: 'aes-256-gcm',
    iv: iv.toString('base64'),
    tag: cipher.getAuthTag().toString('base64'),
    ciphertext: ciphertext.toString('base64'),
  }
}

async function decryptPiProxyApiKey(
  value: unknown,
  input: Record<string, unknown>,
  token: string,
): Promise<string> {
  const encrypted = value as Partial<EncryptedPiProxyApiKey> | null
  if ((encrypted?.v !== 1 && encrypted?.v !== 2) || encrypted.algorithm !== 'aes-256-gcm') return ''
  if (!encrypted.iv || !encrypted.tag || !encrypted.ciphertext) return ''
  const key = await readOrCreatePiProxyTargetKey()
  const decipher = createDecipheriv('aes-256-gcm', key, Buffer.from(encrypted.iv, 'base64'))
  decipher.setAAD(encrypted.v === 1 ? PI_PROXY_TARGET_LEGACY_AAD : piProxyTargetAad(input, token))
  decipher.setAuthTag(Buffer.from(encrypted.tag, 'base64'))
  return Buffer.concat([
    decipher.update(Buffer.from(encrypted.ciphertext, 'base64')),
    decipher.final(),
  ]).toString('utf8')
}

async function serializePiProxyTarget(
  input: Record<string, unknown>,
  apiKey: string,
  token: string,
): Promise<string> {
  return `${JSON.stringify({
    input,
    apiKeyEncrypted: await encryptPiProxyApiKey(apiKey, input, token),
    token,
  }, null, 2)}\n`
}

interface CommandExecution {
  command: string
  args: string[]
  windowsVerbatimArguments?: WindowsCommandExecution['windowsVerbatimArguments']
}

export type CodingAgentId = 'claude-code' | 'codex' | 'dsh' | 'pi'

export interface CodingAgentDefinition {
  id: CodingAgentId
  name: string
  provider: string
  command: string
  packageName: string
}

export interface CodingAgentToolStatus extends CodingAgentDefinition {
  installed: boolean
  version: string
  rawVersion: string
  error?: string
  /** Whether the DeepSeek Harness SDK runtime (full streaming capability) is installed. */
  sdkAvailable?: boolean
}

export interface CodingAgentsStatus {
  tools: CodingAgentToolStatus[]
}

export interface CodingAgentMutationResult extends CodingAgentsStatus {
  success: boolean
  tool: CodingAgentToolStatus
  message?: string
  code?: string
}

export interface CodingAgentConfigFileDefinition {
  key: string
  path: string
  absolutePath: string
  language: string
}

export interface CodingAgentConfigScope {
  profile?: string
  provider?: string
}

export interface CodingAgentConfigFileContent extends CodingAgentConfigFileDefinition {
  content: string
  exists: boolean
  size: number
  profile: string
  provider: string
  rootDir: string
}

export interface CodingAgentLaunchInput extends CodingAgentConfigScope {
  mode?: 'scoped' | 'global'
  model?: string
  workspace?: string | null
  baseUrl?: string
  apiKey?: string
  apiMode?: ApiMode
  reasoningEffort?: string
  sessionId?: string
  agentSessionId?: string
  agentNativeSessionId?: string
  isolateSettings?: boolean
  sessionSource?: 'global_agent' | 'workflow' | 'group_chat'
  groupSystemPrompt?: string
  groupRuntimeScope?: {
    roomId: string
    agentId: string
  }
  /** Internal Pi transport: terminal launches are interactive; Studio runs use RPC. */
  piOutputMode?: 'interactive' | 'rpc'
  approveProjectConfig?: boolean
}

export interface CodingAgentLaunchResult {
  agentId: CodingAgentId
  mode: 'scoped' | 'global'
  profile: string
  provider: string
  model: string
  apiMode?: ApiMode
  rootDir: string
  workspaceDir: string
  command: string
  args: string[]
  env: Record<string, string>
  shellCommand: string
  files: Array<{ key: string; path: string; absolutePath: string }>
  reasoningEffort?: string
  /** Working directory for the runtime process (e.g. a DSH source checkout root). */
  runtimeCwd?: string
}

export interface CodingAgentNativeLaunchResult extends CodingAgentLaunchResult {
  nativeTerminal: true
  terminal: string
}

export interface CodingAgentRunStartResult extends CodingAgentLaunchResult {
  agentSessionId: string
  sessionId: string
  pid: number
}

const TOOL_DEFINITIONS: CodingAgentDefinition[] = [
  {
    id: 'claude-code',
    name: 'Claude Code',
    provider: 'Anthropic',
    command: 'claude',
    packageName: '@anthropic-ai/claude-code',
  },
  {
    id: 'codex',
    name: 'Codex',
    provider: 'OpenAI',
    command: 'codex',
    packageName: '@openai/codex',
  },
  {
    id: 'dsh',
    name: 'DeepSeek Harness',
    provider: 'DeepSeek',
    command: 'dsh',
    packageName: '@deepseek-ai/dsh',
  },
  {
    id: 'pi',
    name: 'Pi',
    provider: 'Pi',
    command: 'pi',
    packageName: '@earendil-works/pi-coding-agent',
  },
]

const CONFIG_FILE_DEFINITIONS: Record<CodingAgentId, Array<Omit<CodingAgentConfigFileDefinition, 'absolutePath'> & { scopedPath: string }>> = {
  'claude-code': [
    { key: 'settings', path: '~/.claude/settings.json', scopedPath: 'settings.json', language: 'json' },
    { key: 'mcp', path: '~/.claude/mcp.json', scopedPath: 'mcp.json', language: 'json' },
    { key: 'prompt', path: '~/.claude/hermes-rules.md', scopedPath: 'hermes-rules.md', language: 'markdown' },
  ],
  codex: [
    { key: 'auth', path: '~/.codex/auth.json', scopedPath: 'auth.json', language: 'json' },
    { key: 'config', path: '~/.codex/config.toml', scopedPath: 'config.toml', language: 'ini' },
    { key: 'agents', path: '~/.codex/AGENTS.md', scopedPath: 'AGENTS.md', language: 'markdown' },
  ],
  dsh: [
    { key: 'settings', path: '~/.dsh/settings.yaml', scopedPath: 'settings.yaml', language: 'yaml' },
    { key: 'patch', path: '~/.dsh/cordis.patch.yml', scopedPath: 'cordis.patch.yml', language: 'yaml' },
    { key: 'credentials', path: '~/.dsh/.credentials.yaml', scopedPath: '.credentials.yaml', language: 'yaml' },
    { key: 'cordis', path: '~/.deepseek-harness/cordis.yml', scopedPath: 'cordis.yml', language: 'yaml' },
  ],
  pi: [
    { key: 'auth', path: '~/.pi/agent/auth.json', scopedPath: 'auth.json', language: 'json' },
    { key: 'settings', path: '~/.pi/agent/settings.json', scopedPath: 'settings.json', language: 'json' },
    { key: 'agents', path: '~/.pi/agent/AGENTS.md', scopedPath: 'AGENTS.md', language: 'markdown' },
    { key: 'mcp', path: '~/.pi/agent/mcp.json', scopedPath: 'mcp.json', language: 'json' },
  ],
}

const installingTools = new Set<CodingAgentId>()
const deletingTools = new Set<CodingAgentId>()
let cachedGlobalNpmBin: string | null | undefined
let cachedLoginShellPath: string | null | undefined
const MAX_CONFIG_FILE_SIZE = parseInt(process.env.MAX_EDIT_SIZE || '', 10) || 10 * 1024 * 1024

function getNodeBinDir() {
  return dirname(process.execPath)
}

function getNodePrefix() {
  return process.platform === 'win32' ? getNodeBinDir() : dirname(getNodeBinDir())
}

function getHomebrewPrefix() {
  const match = process.execPath.match(/^(.*)\/Cellar\/[^/]+\/[^/]+\/bin\/node$/)
  return match?.[1] || null
}

function getNpmCliCandidates() {
  const prefix = getNodePrefix()
  const homebrewPrefix = getHomebrewPrefix()

  return process.platform === 'win32'
    ? [
        join(prefix, 'node_modules', 'npm', 'bin', 'npm-cli.js'),
        join(getNodeBinDir(), 'node_modules', 'npm', 'bin', 'npm-cli.js'),
      ]
    : [
        join(prefix, 'lib', 'node_modules', 'npm', 'bin', 'npm-cli.js'),
        ...(homebrewPrefix ? [join(homebrewPrefix, 'lib', 'node_modules', 'npm', 'bin', 'npm-cli.js')] : []),
      ]
}

function getNpmCliPath() {
  return getNpmCliCandidates().find(existsSync) || null
}

function getNpmBin() {
  return process.platform === 'win32' ? 'npm.cmd' : 'npm'
}

function getGlobalConfigHome() {
  return process.env.HERMES_CODING_AGENT_GLOBAL_HOME?.trim() || homedir()
}

function compareNodeVersionDesc(left: string, right: string): number {
  const leftParts = left.replace(/^v/, '').split('.').map(part => Number.parseInt(part, 10) || 0)
  const rightParts = right.replace(/^v/, '').split('.').map(part => Number.parseInt(part, 10) || 0)
  for (let index = 0; index < Math.max(leftParts.length, rightParts.length); index += 1) {
    const diff = (rightParts[index] || 0) - (leftParts[index] || 0)
    if (diff !== 0) return diff
  }
  return right.localeCompare(left)
}

function getNvmNodeBinPaths(): string {
  if (process.env.HERMES_DESKTOP !== 'true' || process.platform === 'win32') return ''

  const nvmDir = process.env.NVM_DIR?.trim() || join(homedir(), '.nvm')
  const versionsDir = join(nvmDir, 'versions', 'node')
  if (!existsSync(versionsDir)) return ''

  try {
    return readdirSync(versionsDir, { withFileTypes: true })
      .filter(entry => entry.isDirectory())
      .map(entry => entry.name)
      .sort(compareNodeVersionDesc)
      .map(version => join(versionsDir, version, 'bin'))
      .filter(binDir => existsSync(binDir))
      .join(delimiter)
  } catch {
    return ''
  }
}

function getLoginShellCandidates(): string[] {
  if (process.platform === 'win32') return []
  return [
    process.env.SHELL || '',
    '/bin/zsh',
    '/bin/bash',
    '/usr/bin/zsh',
    '/usr/bin/bash',
  ].filter(Boolean)
}

function getLoginShell(): string | null {
  for (const shell of [...new Set(getLoginShellCandidates())]) {
    if (shell.startsWith('/') && existsSync(shell)) return shell
  }
  return null
}

async function getLoginShellPath(): Promise<string | null> {
  if (process.env.HERMES_DESKTOP !== 'true' || process.platform === 'win32') return null
  if (typeof cachedLoginShellPath !== 'undefined') return cachedLoginShellPath

  const shell = getLoginShell()
  if (!shell) {
    cachedLoginShellPath = null
    return cachedLoginShellPath
  }

  try {
    const { stdout } = await execFileAsync(shell, ['-lc', 'printf %s "$PATH"'], {
      encoding: 'utf-8',
      timeout: 3000,
      windowsHide: true,
    })
    cachedLoginShellPath = stdout.trim() || null
  } catch {
    cachedLoginShellPath = null
  }
  return cachedLoginShellPath
}

function getDesktopCommonBinPaths(): string[] {
  if (process.env.HERMES_DESKTOP !== 'true' || process.platform === 'win32') return []
  const home = homedir()
  return [
    join(home, '.npm-global', 'bin'),
    join(home, '.local', 'bin'),
    join(home, '.yarn', 'bin'),
    join(home, '.config', 'yarn', 'global', 'node_modules', '.bin'),
    join(home, '.pnpm'),
    join(home, 'Library', 'pnpm'),
    '/opt/homebrew/bin',
    '/usr/local/bin',
  ]
}

function prependPathEntries(env: NodeJS.ProcessEnv, entries: Array<string | null | undefined>) {
  const pathKey = Object.keys(env).find(key => key.toLowerCase() === 'path') || 'PATH'
  const currentPath = env[pathKey] || ''
  const existing = new Set(currentPath.split(delimiter).filter(Boolean))
  const prepended: string[] = []

  for (const entry of entries) {
    if (!entry) continue
    for (const segment of entry.split(delimiter).map(item => item.trim()).filter(Boolean)) {
      if (existing.has(segment) || prepended.includes(segment)) continue
      prepended.push(segment)
    }
  }

  if (prepended.length > 0) {
    env[pathKey] = currentPath ? `${prepended.join(delimiter)}${delimiter}${currentPath}` : prepended.join(delimiter)
  }
}

function nodeEnvironmentMissingError(): Error {
  const err = new Error('Node/npm environment was not detected. Please install Node.js and try again.')
  ;(err as any).code = NODE_ENVIRONMENT_MISSING_CODE
  return err
}

function isNodeEnvironmentMissingError(err: any): boolean {
  const text = [
    err?.code,
    err?.message,
    typeof err?.stderr === 'string' ? err.stderr : '',
    typeof err?.stdout === 'string' ? err.stdout : '',
  ].filter(Boolean).join('\n').toLowerCase()
  return text.includes('enoent') ||
    text.includes('spawn npm') ||
    text.includes('npm: command not found') ||
    text.includes('npm not found') ||
    text.includes('node: command not found') ||
    text.includes('node not found')
}

function npmCliFromNpmBin(npmBin: string): { node: string; npmCli: string } | null {
  const binDir = dirname(npmBin)
  if (process.platform === 'win32') {
    const node = join(binDir, 'node.exe')
    const npmCli = join(binDir, 'node_modules', 'npm', 'bin', 'npm-cli.js')
    return existsSync(node) && existsSync(npmCli) ? { node, npmCli } : null
  }

  const node = join(binDir, 'node')
  const npmCli = join(dirname(binDir), 'lib', 'node_modules', 'npm', 'bin', 'npm-cli.js')
  return existsSync(node) && existsSync(npmCli) ? { node, npmCli } : null
}

function normalizeScopeSegment(value: string | undefined, fallback: string, label: string): string {
  // Replace invalid filename characters with underscores
  // Windows invalid chars: < > : " / \ | ? *
  // Additional problematic chars: control characters
  const sanitizedValue = String(value || '').trim().replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
  const segment = sanitizedValue || fallback

  if (
    segment === '.' ||
    segment === '..' ||
    segment.includes('\0')
  ) {
    const err = new Error(`Invalid ${label}`)
    ;(err as any).status = 400
    throw err
  }
  if (segment.length > 128) {
    const err = new Error(`${label} is too long`)
    ;(err as any).status = 400
    throw err
  }
  return segment
}

function normalizeProviderIdentity(value: string | undefined): string {
  const provider = String(value || '').trim() || 'default'
  if (/[\x00-\x1f\x7f-\x9f]/.test(provider)) {
    const err = new Error('Invalid provider')
    ;(err as any).status = 400
    throw err
  }
  if (provider.length > 128) {
    const err = new Error('provider is too long')
    ;(err as any).status = 400
    throw err
  }
  return provider
}

function normalizeConfigScope(scope: CodingAgentConfigScope = {}): Required<CodingAgentConfigScope> {
  return {
    profile: normalizeScopeSegment(scope.profile, 'default', 'profile'),
    provider: normalizeScopeSegment(scope.provider, 'default', 'provider'),
  }
}

function slugProviderName(value: string): string {
  return String(value || '').trim().toLowerCase().replace(/ /g, '-')
}

function providerKeyWithoutCustomPrefix(providerKey: string): string {
  if (providerKey.startsWith('custom:')) return providerKey.slice('custom:'.length)
  if (providerKey.startsWith('custom_')) return providerKey.slice('custom_'.length)
  return providerKey
}

function providerLookupCandidates(provider: string): string[] {
  const trimmed = String(provider || '').trim()
  const withoutCustom = providerKeyWithoutCustomPrefix(trimmed)
  return [...new Set([
    trimmed,
    withoutCustom,
    withoutCustom ? `custom:${withoutCustom}` : '',
    withoutCustom ? `custom_${withoutCustom}` : '',
  ].filter(Boolean))]
}

function parseEnvValue(envContent: string, key: string): string {
  if (!key) return ''
  const lines = envContent.split(/\r?\n/)
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) continue
    const eqIndex = trimmed.indexOf('=')
    if (eqIndex === -1) continue
    if (trimmed.slice(0, eqIndex).trim() !== key) continue
    const raw = trimmed.slice(eqIndex + 1).trim()
    if (
      (raw.startsWith('"') && raw.endsWith('"')) ||
      (raw.startsWith("'") && raw.endsWith("'"))
    ) {
      return raw.slice(1, -1)
    }
    return raw
  }
  return ''
}

function inferLaunchApiMode(provider: string, baseUrl: string, fallback: ApiMode = 'chat_completions'): ApiMode {
  const providerKey = String(provider || '').toLowerCase()
  const normalizedBaseUrl = String(baseUrl || '').toLowerCase()
  if (
    providerKey.includes('claude') ||
    providerKey === 'anthropic' ||
    normalizedBaseUrl.includes('anthropic') ||
    normalizedBaseUrl.includes('/anthropic')
  ) {
    return 'anthropic_messages'
  }
  if (
    providerKey === 'deepseek' ||
    providerKey === 'lmstudio' ||
    normalizedBaseUrl.includes('deepseek') ||
    normalizedBaseUrl.includes('127.0.0.1') ||
    normalizedBaseUrl.includes('localhost')
  ) {
    return 'chat_completions'
  }
  return fallback
}

function providerPresetHost(value?: string): string {
  const url = String(value || '').trim()
  if (!url) return ''
  try {
    return new URL(url).hostname.toLowerCase()
  } catch {
    return ''
  }
}

function belongsToDifferentBuiltinProvider(provider: string, baseUrl: string): boolean {
  const providerKey = providerKeyWithoutCustomPrefix(String(provider || '').trim().toLowerCase())
  if (!providerKey || provider !== providerKey) return false
  const currentPreset = PROVIDER_PRESETS.find(item => item.value === providerKey)
  if (!currentPreset) return false
  const inputHost = providerPresetHost(baseUrl)
  const currentHost = providerPresetHost(currentPreset.base_url)
  if (!inputHost || !currentHost || inputHost === currentHost) return false
  return PROVIDER_PRESETS.some((item) => (
    item.value !== providerKey &&
    providerPresetHost(item.base_url) === inputHost
  ))
}

async function resolveStoredProviderLaunchInput(
  input: CodingAgentLaunchInput & { sessionId: string },
  existingSession: HermesSessionRow | null,
): Promise<CodingAgentLaunchInput & { sessionId: string }> {
  if (input.mode === 'global') return input

  const profile = String(input.profile || existingSession?.profile || 'default').trim() || 'default'
  const inputProvider = String(input.provider || '').trim()
  const storedProvider = String(existingSession?.provider || '').trim()
  const provider = String(inputProvider || storedProvider).trim()
  const model = String(input.model || existingSession?.model || '').trim()
  const workspace = input.workspace || existingSession?.workspace || undefined
  let baseUrl = String(input.baseUrl || '').trim()
  let apiKey = String(input.apiKey || '').trim()
  const storedApiMode = !inputProvider || inputProvider === storedProvider
    ? normalizeStoredLaunchApiMode(existingSession?.api_mode)
    : undefined
  let apiMode = input.apiMode || storedApiMode
  let canonicalProvider = provider
  const ignoredStaleProviderRuntime = belongsToDifferentBuiltinProvider(provider, baseUrl)
  if (ignoredStaleProviderRuntime) {
    baseUrl = ''
    apiKey = ''
  }

  if (!provider || (baseUrl && apiKey && apiMode)) {
    return { ...input, profile, provider: provider || input.provider, model: model || input.model, workspace, baseUrl, apiKey, apiMode }
  }

  let config: Record<string, any> = {}
  try {
    config = await readConfigYamlForProfile(profile)
  } catch {}
  const envContent = await safeReadFile(join(getProfileDir(profile), '.env')) || ''
  const normalizedProvider = providerKeyWithoutCustomPrefix(provider)
  const preset = PROVIDER_PRESETS.find(item => item.value === normalizedProvider)
  const candidates = providerLookupCandidates(provider)

  const customProviders = getCompatibleCustomProviders(config)
  const customEntry = customProviders.find((entry) => {
    const name = slugProviderName(String(entry?.name || ''))
    return candidates.includes(`custom:${name}`) || candidates.includes(`custom_${name}`) || candidates.includes(name)
  })
  if (customEntry) {
    canonicalProvider = `custom:${slugProviderName(String(customEntry.name || normalizedProvider))}`
    if (!baseUrl) baseUrl = String(customEntry.base_url || '').trim()
    if (!apiKey) apiKey = String(customEntry.api_key || '').trim()
    if (!apiKey) {
      const keyEnv = String(customEntry.key_env || '').trim()
      if (keyEnv) apiKey = parseEnvValue(envContent, keyEnv)
    }
    if (!apiMode) {
      apiMode = normalizeLaunchApiMode(
        customEntry.api_mode,
        preset?.api_mode || inferLaunchApiMode(canonicalProvider, baseUrl, 'chat_completions'),
      )
    }
  }

  const canonicalProviderKey = providerKeyWithoutCustomPrefix(canonicalProvider)
  const canonicalPreset = PROVIDER_PRESETS.find(item => item.value === canonicalProviderKey) || preset
  const envMapping = PROVIDER_ENV_MAP[canonicalProviderKey]
  if (!baseUrl) {
    baseUrl = envMapping?.base_url_env
      ? parseEnvValue(envContent, envMapping.base_url_env) || canonicalPreset?.base_url || ''
      : canonicalPreset?.base_url || ''
  }
  if (!apiKey && envMapping?.api_key_env) {
    apiKey = parseEnvValue(envContent, envMapping.api_key_env)
  }
  if (!apiMode) {
    apiMode = normalizeLaunchApiMode(
      canonicalPreset?.api_mode,
      inferLaunchApiMode(canonicalProvider, baseUrl, 'chat_completions'),
    )
  }

  return {
    ...input,
    profile,
    provider: canonicalProvider,
    model: model || input.model,
    workspace,
    baseUrl: baseUrl || (ignoredStaleProviderRuntime ? '' : input.baseUrl),
    apiKey: apiKey || (ignoredStaleProviderRuntime ? '' : input.apiKey),
    apiMode,
  }
}

function normalizeStoredLaunchApiMode(value: unknown): ApiMode | undefined {
  if (!value) return undefined
  try {
    return normalizeLaunchApiMode(value, 'chat_completions')
  } catch {
    return undefined
  }
}

function normalizeLaunchApiMode(value: unknown, fallback: ApiMode): ApiMode {
  if (!value) return fallback
  const mode = String(value).trim() as ApiMode
  if (LAUNCH_API_MODES.has(mode)) return mode
  if (mode === 'codex_app_server') return 'codex_responses'

  const err = new Error('Invalid API protocol')
  ;(err as any).status = 400
  throw err
}

function storedCodingAgentMode(session: HermesSessionRow | null): 'scoped' | 'global' {
  if (session?.agent_mode === 'global' || session?.agent_mode === 'scoped') return session.agent_mode
  return session?.provider === 'global' ? 'global' : 'scoped'
}

function persistedAgentId(id: string): 'claude' | 'codex' | 'dsh' | 'pi' {
  if (id === 'codex') return 'codex'
  if (id === 'dsh') return 'dsh'
  if (id === 'pi') return 'pi'
  return 'claude'
}

function makeAgentSessionId(): string {
  return `coding_agent_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

function getScopedConfigRoot(id: CodingAgentId, scope: Required<CodingAgentConfigScope>): string {
  return join(getWebUiHome(), CODING_AGENT_HOME_DIR, 'model', scope.profile, scope.provider, id)
}

function getScopedRuntimeConfigRoot(
  id: CodingAgentId,
  scope: Required<CodingAgentConfigScope>,
  input: Pick<CodingAgentLaunchInput, 'sessionId' | 'agentSessionId' | 'groupRuntimeScope'>,
): string {
  const groupRoomId = String(input.groupRuntimeScope?.roomId || '').trim()
  const groupAgentId = String(input.groupRuntimeScope?.agentId || '').trim()
  if (groupRoomId && groupAgentId) {
    const stableSegment = (value: string) => {
      const readable = value.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 48) || 'scope'
      const digest = createHash('sha256').update(value).digest('hex').slice(0, 12)
      return `${readable}_${digest}`
    }
    return join(
      getScopedConfigRoot(id, scope),
      'group-chat',
      stableSegment(groupRoomId),
      stableSegment(groupAgentId),
    )
  }
  const rootDir = getScopedConfigRoot(id, scope)
  const sessionId = String(input.sessionId || '').trim()
  const agentSessionId = String(input.agentSessionId || '').trim()
  if ((!sessionId || !agentSessionId) && id === 'pi') {
    const runtimeKey = createHash('sha256')
      .update(JSON.stringify([
        sessionId || randomUUID(),
        agentSessionId || randomUUID(),
        process.pid,
        Date.now(),
      ]))
      .digest('hex')
    return join(rootDir, 'runs', runtimeKey)
  }
  if (!sessionId || !agentSessionId) return rootDir
  const runtimeKey = createHash('sha256')
    .update(JSON.stringify([sessionId, agentSessionId]))
    .digest('hex')
  return join(rootDir, 'runs', runtimeKey)
}

function getScopedWorkspaceRoot(scope: Required<CodingAgentConfigScope>): string {
  return join(getWebUiHome(), CODING_AGENT_HOME_DIR, 'workspace', scope.profile, scope.provider)
}

function resolveLaunchWorkspaceRoot(scope: Required<CodingAgentConfigScope>, workspace?: string | null): string {
  const customWorkspace = String(workspace || '').trim()
  if (customWorkspace) {
    if (customWorkspace.includes('\0')) {
      const err = new Error('Invalid workspace')
      ;(err as any).status = 400
      throw err
    }
    return customWorkspace
  }
  return getScopedWorkspaceRoot(scope)
}

function displayNameForModel(model: string): string {
  const trimmed = model.trim()
  if (!trimmed) return 'Model'
  const leaf = trimmed.split('/').filter(Boolean).pop() || trimmed
  return leaf
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

function codexCatalogEntry(input: {
  model: string
  displayName: string
  contextWindow: number
  priority: number
}) {
  return {
    slug: input.model,
    display_name: input.displayName,
    description: input.displayName,
    default_reasoning_level: 'medium',
    supported_reasoning_levels: [
      { effort: 'none', description: 'Disable provider-side reasoning when supported' },
      { effort: 'minimal', description: 'Use the smallest provider-side reasoning budget when supported' },
      { effort: 'low', description: 'Fast responses with lighter reasoning' },
      { effort: 'medium', description: 'Balances speed and reasoning depth for everyday tasks' },
      { effort: 'high', description: 'Greater reasoning depth for complex problems' },
      { effort: 'xhigh', description: 'Extra high reasoning depth for complex problems' },
      { effort: 'max', description: 'Maximum reasoning depth for the hardest quality-first tasks' },
    ],
    shell_type: 'shell_command',
    visibility: 'list',
    supported_in_api: true,
    priority: 1000 + input.priority,
    additional_speed_tiers: [],
    service_tiers: [],
    default_service_tier: null,
    availability_nux: null,
    upgrade: null,
    base_instructions: CODEX_CATALOG_BASE_INSTRUCTIONS,
    model_messages: {
      instructions_template: '{{ base_instructions }}\n\n{{ personality }}',
      instructions_variables: {
        base_instructions: CODEX_CATALOG_BASE_INSTRUCTIONS,
        personality: '',
        personality_default: '',
        personality_friendly: '',
        personality_pragmatic: '',
      },
    },
    supports_reasoning_summaries: true,
    default_reasoning_summary: 'auto',
    support_verbosity: true,
    default_verbosity: 'low',
    apply_patch_tool_type: 'freeform',
    web_search_tool_type: 'text_and_image',
    truncation_policy: { mode: 'tokens', limit: 10_000 },
    supports_parallel_tool_calls: true,
    supports_image_detail_original: true,
    context_window: input.contextWindow,
    max_context_window: input.contextWindow,
    effective_context_window_percent: 95,
    experimental_supported_tools: [],
    input_modalities: ['text', 'image'],
    supports_search_tool: true,
  }
}

function buildCodexModelCatalog(input: {
  profile: string
  provider: string
  model: string
  presetModels: string[]
}) {
  const models = [...new Set([input.model, ...input.presetModels].map(item => item.trim()).filter(Boolean))]
  return {
    models: models.map((model, index) => codexCatalogEntry({
      model,
      displayName: displayNameForModel(model),
      contextWindow: getModelContextLength({ profile: input.profile, provider: input.provider, model }),
      priority: index,
    })),
  }
}

function hasRootPrivileges(): boolean {
  if (process.platform === 'win32') return false
  const uid = typeof process.getuid === 'function' ? process.getuid() : null
  const euid = typeof process.geteuid === 'function' ? process.geteuid() : null
  return uid === 0 || euid === 0
}

function claudeCodePermissionArgs(): string[] {
  return hasRootPrivileges() ? CLAUDE_CODE_ROOT_PERMISSION_ARGS : CLAUDE_CODE_SKIP_PERMISSIONS_ARGS
}

function expandHomePath(path: string): string {
  if (path === '~') return getGlobalConfigHome()
  if (path.startsWith('~/')) return join(getGlobalConfigHome(), path.slice(2))
  return path
}

function hermesPromptDocument(systemPrompt = getSystemPrompt()): string {
  return [
    HERMES_PROMPT_BLOCK_BEGIN,
    systemPrompt.trim(),
    HERMES_PROMPT_BLOCK_END,
    '',
  ].join('\n')
}

function upsertManagedMarkdownBlock(existing: string, block: string): string {
  const normalizedBlock = block.endsWith('\n') ? block : `${block}\n`
  const start = existing.indexOf(HERMES_PROMPT_BLOCK_BEGIN)
  const end = existing.indexOf(HERMES_PROMPT_BLOCK_END)
  if (start >= 0 && end >= start) {
    const afterEnd = end + HERMES_PROMPT_BLOCK_END.length
    const before = existing.slice(0, start).replace(/\s*$/, '')
    const after = existing.slice(afterEnd).replace(/^\s*/, '')
    return [before, normalizedBlock.trimEnd(), after].filter(Boolean).join('\n\n') + '\n'
  }
  const trimmedExisting = existing.replace(/\s*$/, '')
  if (!trimmedExisting) return normalizedBlock
  return `${trimmedExisting}\n\n${normalizedBlock}`
}

async function writeManagedPromptFile(definition: CodingAgentConfigFileDefinition): Promise<{ key: string; path: string; absolutePath: string }> {
  let existing = ''
  try {
    existing = await readFile(definition.absolutePath, 'utf-8')
  } catch (err: any) {
    if (err?.code !== 'ENOENT') throw err
  }
  const next = upsertManagedMarkdownBlock(existing, hermesPromptDocument())
  if (next !== existing) {
    await mkdir(dirname(definition.absolutePath), { recursive: true })
    await writeFile(definition.absolutePath, next, 'utf-8')
  }
  return {
    key: definition.key,
    path: definition.path,
    absolutePath: definition.absolutePath,
  }
}

async function ensureGlobalCodingAgentPromptFile(id: CodingAgentId): Promise<Array<{ key: string; path: string; absolutePath: string }>> {
  if (id !== 'claude-code') return []
  const definition = getLiveConfigFileDefinition(id, 'prompt')
  if (!definition) return []
  return [await writeManagedPromptFile(definition)]
}

function shellQuote(value: string): string {
  if (/^[A-Za-z0-9_./:=@+-]+$/.test(value)) return value
  return `'${value.replace(/'/g, `'\\''`)}'`
}

function powerShellQuote(value: string): string {
  return `'${value.replace(/'/g, "''")}'`
}

function tomlString(value: string): string {
  return JSON.stringify(value)
}

function yamlString(value: string): string {
  return JSON.stringify(value)
}

function tomlMultilineString(value: string): string {
  const normalized = String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\\/g, '\\\\')
    .replace(/"""/g, '\\"\\"\\"')
  return `"""\n${normalized}\n"""`
}

function tomlStringArray(values: string[]): string {
  return `[${values.map(tomlString).join(', ')}]`
}

function tomlInlineStringTable(values: Record<string, string>): string {
  return `{ ${Object.entries(values).map(([key, value]) => `${key} = ${tomlString(value)}`).join(', ')} }`
}

function isDesktopRuntime(): boolean {
  return String(process.env.HERMES_DESKTOP || '').trim().toLowerCase() === 'true'
}

function candidateBundledMcpScripts(): string[] {
  return [
    process.env.HERMES_WEB_UI_MCP_BIN,
    join(process.cwd(), 'bin/hermes-studio-mcp.mjs'),
    join(__dirname, '../../bin/hermes-studio-mcp.mjs'),
    join(__dirname, '../../../../../bin/hermes-studio-mcp.mjs'),
    join(process.cwd(), 'bin/hermes-web-ui-mcp.mjs'),
    join(__dirname, '../../bin/hermes-web-ui-mcp.mjs'),
    join(__dirname, '../../../../../bin/hermes-web-ui-mcp.mjs'),
  ].filter((value): value is string => !!value)
}

function bundledMcpScriptPath(): string | null {
  return candidateBundledMcpScripts().find(candidate => existsSync(candidate)) || null
}

function runtimeNodePath(): string | null {
  const node = process.env.HERMES_AGENT_NODE?.trim()
  return node || null
}

function hermesMcpCommandConfig(toolset: string): { command: string; args?: string[] } {
  const script = bundledMcpScriptPath()
  if (script) return { command: runtimeNodePath() || process.execPath, args: [script, toolset] }
  if (isDesktopRuntime()) return { command: 'hermes-studio-mcp', args: [toolset] }
  return { command: 'hermes-studio-mcp', args: [toolset] }
}

function hermesMcpServerConfig(profile: string, serverName: string, toolset: string): { command: string; args?: string[]; env: Record<string, string> } {
  const appHome = getWebUiHome()
  return {
    ...hermesMcpCommandConfig(toolset),
    env: {
      HERMES_WEB_UI_URL: `http://127.0.0.1:${process.env.PORT || '6060'}`,
      HERMES_WEB_UI_HOME: appHome,
      HERMES_WEBUI_STATE_DIR: appHome,
      HERMES_WEB_UI_PROFILE: profile,
      HERMES_MCP_SERVER_NAME: serverName,
      HERMES_MCP_TOOLSET: toolset,
      [HERMES_MCP_MANAGED_ENV_KEY]: '1',
    },
  }
}

function isManagedHermesMcpServer(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const server = value as Record<string, any>
  if (server.env && typeof server.env === 'object' && server.env[HERMES_MCP_MANAGED_ENV_KEY] === '1') return true
  return typeof server.command === 'string' && LEGACY_HERMES_MCP_COMMANDS.has(server.command)
}

function normalizeClaudeMcpServer(server: unknown): unknown {
  if (!server || typeof server !== 'object' || Array.isArray(server)) return server
  const normalized = { ...(server as Record<string, unknown>) }
  if (normalized.type === 'streamableHttp') normalized.type = 'http'
  return normalized
}

function parseClaudeMcpServers(existingContent: string | null | undefined = ''): Record<string, unknown> {
  if (!existingContent?.trim()) return {}
  try {
    const parsed = JSON.parse(existingContent)
    if (!parsed?.mcpServers || typeof parsed.mcpServers !== 'object' || Array.isArray(parsed.mcpServers)) return {}
    return Object.fromEntries(Object.entries(parsed.mcpServers)
      .filter(([name, server]) => {
        if (HERMES_MCP_SERVER_NAMES.has(name)) return false
        if (LEGACY_HERMES_MCP_SERVER_NAMES.has(name)) return false
        return !isManagedHermesMcpServer(server)
      })
      .map(([name, server]) => [name, normalizeClaudeMcpServer(server)]))
  } catch {
    return {}
  }
}

function inheritClaudeSettings(existingContent: string | null | undefined = ''): Record<string, unknown> {
  if (!existingContent?.trim()) return {}
  try {
    const parsed = JSON.parse(existingContent)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const inherited: Record<string, unknown> = {}
    const enabledServers = (parsed as any).enabledMcpjsonServers
    if (Array.isArray(enabledServers)) inherited.enabledMcpjsonServers = enabledServers.map(String).filter(Boolean)
    const plugins = (parsed as any).plugins
    if (plugins && typeof plugins === 'object' && !Array.isArray(plugins)) inherited.plugins = plugins
    const enabledPlugins = (parsed as any).enabledPlugins
    if (enabledPlugins && typeof enabledPlugins === 'object' && !Array.isArray(enabledPlugins)) inherited.enabledPlugins = enabledPlugins
    return inherited
  } catch {
    return {}
  }
}

function claudeMcpConfigJson(profile: string, ...existingContents: Array<string | null | undefined>): string {
  const mcpServers: Record<string, unknown> = {}
  for (const content of existingContents) {
    Object.assign(mcpServers, parseClaudeMcpServers(content))
  }
  for (const server of HERMES_MCP_SERVERS) {
    mcpServers[server.name] = hermesMcpServerConfig(profile, server.name, server.toolset)
  }
  return `${JSON.stringify({ mcpServers }, null, 2)}\n`
}

function parseCodexExternalMcpBlocks(...contents: Array<string | null | undefined>): string[] {
  const blockByServer = new Map<string, string>()

  for (const content of contents) {
    if (!content?.trim()) continue
    let currentServer = ''
    let currentLines: string[] = []
    const flush = () => {
      if (!currentServer || currentLines.length === 0) return
      const block = currentLines.join('\n').trim()
      const isManaged = block.includes(`${HERMES_MCP_MANAGED_ENV_KEY}`)
      if (!HERMES_MCP_SERVER_NAMES.has(currentServer) && !LEGACY_HERMES_MCP_SERVER_NAMES.has(currentServer) && !isManaged) {
        blockByServer.set(currentServer, block)
      }
    }

    for (const line of content.split(/\r?\n/)) {
      const mcpMatch = line.match(/^\s*\[mcp_servers\.([^\].]+)(\.[^\]]+)?\]\s*$/)
      if (mcpMatch) {
        const nextServer = mcpMatch[1]
        const isSubtable = Boolean(mcpMatch[2])
        if (currentServer && nextServer === currentServer && isSubtable) {
          currentLines.push(line)
          continue
        }
        flush()
        currentServer = nextServer
        currentLines = [line]
        continue
      }
      if (/^\s*\[/.test(line)) {
        flush()
        currentServer = ''
        currentLines = []
        continue
      }
      if (currentServer) currentLines.push(line)
    }
    flush()
  }

  return Array.from(blockByServer.values()).filter(Boolean)
}

function codexMcpConfigToml(profile: string, ...externalContents: Array<string | null | undefined>): string {
  const blocks: string[] = [...parseCodexExternalMcpBlocks(...externalContents)]
  for (const item of HERMES_MCP_SERVERS) {
    const server = hermesMcpServerConfig(profile, item.name, item.toolset)
    const lines = [
      `[mcp_servers.${item.name}]`,
      `command = ${tomlString(server.command)}`,
    ]
    if (server.args?.length) lines.push(`args = ${tomlStringArray(server.args)}`)
    lines.push('startup_timeout_sec = 120')
    lines.push(`env = ${tomlInlineStringTable(server.env)}`)
    lines.push('')
    blocks.push(lines.join('\n'))
  }
  return blocks.join('\n')
}

function getPiMcpAdapterRoot(): string {
  return join(getWebUiHome(), CODING_AGENT_HOME_DIR, 'pi-mcp-adapter')
}

function getPiMcpAdapterEntry(): string {
  return join(getPiMcpAdapterRoot(), 'node_modules', 'pi-mcp-adapter', 'index.ts')
}

function piSettingsConfig(existingContent = '', runtimeExtensionPath = ''): string {
  let existing: Record<string, unknown> = {}
  try {
    const parsed = JSON.parse(existingContent)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) existing = parsed
  } catch {}
  const configuredExtensions = Array.isArray(existing.extensions)
    ? existing.extensions.filter(value => typeof value === 'string' && value.trim())
    : []
  return `${JSON.stringify({
    ...existing,
    defaultProjectTrust: 'never',
    enableSkillCommands: true,
    extensions: [...new Set([
      ...configuredExtensions,
      getPiMcpAdapterEntry(),
      ...(runtimeExtensionPath ? [runtimeExtensionPath] : []),
    ])],
  }, null, 2)}\n`
}

function piStudioRuntimeExtension(): string {
  return [
    'import { readFileSync } from "node:fs";',
    '',
    'export default function hermesStudioRuntime(pi: any) {',
    '  pi.on("before_agent_start", async (event: any) => {',
    '    const path = String(process.env.HERMES_PI_DYNAMIC_PROMPT_FILE || "").trim();',
    '    if (!path) return;',
    '    let instructions = "";',
    '    try { instructions = readFileSync(path, "utf8").trim(); } catch {}',
    '    if (!instructions) return;',
    '    return { systemPrompt: `${event.systemPrompt}\\n\\n${instructions}` };',
    '  });',
    '}',
    '',
  ].join('\n')
}

const PI_RUNTIME_MCP_SETTINGS: Record<string, unknown> = {
  hostConfigDiscovery: 'off',
  toolPrefix: 'none',
  directTools: false,
  scriptMode: false,
  outputGuard: true,
  showStatusIcon: false,
  mcpFooterStatus: 'off',
  requestTimeoutMs: 120_000,
}

function parsePiExternalMcpConfig(...contents: Array<string | null | undefined>): {
  settings: Record<string, unknown>
  mcpServers: Record<string, unknown>
} {
  const settings: Record<string, unknown> = {}
  const mcpServers: Record<string, unknown> = {}

  for (const content of contents) {
    if (!content?.trim()) continue
    try {
      const parsed = JSON.parse(content)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) continue
      if (parsed.settings && typeof parsed.settings === 'object' && !Array.isArray(parsed.settings)) {
        for (const [key, value] of Object.entries(parsed.settings)) {
          if (key in PI_RUNTIME_MCP_SETTINGS) continue
          settings[key] = value
        }
      }
      if (parsed.mcpServers && typeof parsed.mcpServers === 'object' && !Array.isArray(parsed.mcpServers)) {
        for (const [name, server] of Object.entries(parsed.mcpServers)) {
          if (HERMES_MCP_SERVER_NAMES.has(name)) continue
          if (LEGACY_HERMES_MCP_SERVER_NAMES.has(name)) continue
          if (isManagedHermesMcpServer(server)) continue
          mcpServers[name] = server
        }
      }
    } catch {
      // Invalid user JSON remains editable and is ignored only for launch-time merging.
    }
  }

  return { settings, mcpServers }
}

function piUserMcpConfig(...existingContents: Array<string | null | undefined>): string {
  const external = parsePiExternalMcpConfig(...existingContents)
  return `${JSON.stringify({
    ...(Object.keys(external.settings).length > 0 ? { settings: external.settings } : {}),
    mcpServers: external.mcpServers,
  }, null, 2)}\n`
}

function piMcpConfig(profile: string, ...externalContents: Array<string | null | undefined>): string {
  const external = parsePiExternalMcpConfig(...externalContents)
  const mcpServers = Object.fromEntries(HERMES_MCP_SERVERS.map((item) => {
    const server = hermesMcpServerConfig(profile, item.name, item.toolset)
    const requestTimeoutMs = item.toolset === 'api' || item.toolset === 'use' ? 120_000 : 1_860_000
    return [item.name, {
      ...server,
      lifecycle: 'lazy',
      directTools: false,
      toolPrefix: 'none',
      requestTimeoutMs,
    }]
  }))
  return `${JSON.stringify({
    settings: {
      ...external.settings,
      ...PI_RUNTIME_MCP_SETTINGS,
    },
    mcpServers: {
      ...external.mcpServers,
      ...mcpServers,
    },
  }, null, 2)}\n`
}

function migratePiRuntimeMcpContent(content: string): string | null {
  try {
    const parsed = JSON.parse(content)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    const servers = parsed.mcpServers
    if (!servers || typeof servers !== 'object' || Array.isArray(servers)) return null
    const managedServers = Object.entries(servers)
      .filter(([name, server]) => HERMES_MCP_SERVER_NAMES.has(name) || isManagedHermesMcpServer(server))
    if (managedServers.length === 0) return null

    const settings = parsed.settings && typeof parsed.settings === 'object' && !Array.isArray(parsed.settings)
      ? parsed.settings
      : {}
    parsed.settings = {
      ...settings,
      ...PI_RUNTIME_MCP_SETTINGS,
    }
    delete parsed.settings.freezeDirectTools

    for (const [, server] of managedServers) {
      if (!server || typeof server !== 'object' || Array.isArray(server)) continue
      const managedServer = server as Record<string, unknown>
      managedServer.lifecycle = 'lazy'
      managedServer.directTools = false
    }

    const migrated = `${JSON.stringify(parsed, null, 2)}\n`
    return migrated === content ? null : migrated
  } catch {
    return null
  }
}

/**
 * Upgrade persisted Pi runtime files created before MCP proxy mode became the
 * default. LazyCat app upgrades preserve /home, so old launchers may otherwise
 * keep registering every MCP tool directly until the user prepares a new run.
 */
export async function migratePersistedPiRuntimeMcpConfigs(): Promise<number> {
  const modelRoot = join(getWebUiHome(), CODING_AGENT_HOME_DIR, 'model')
  if (!existsSync(modelRoot)) return 0

  const candidates: string[] = []
  const directories = (path: string) => {
    try {
      return readdirSync(path, { withFileTypes: true }).filter(entry => entry.isDirectory())
    } catch {
      return []
    }
  }

  for (const profile of directories(modelRoot)) {
    const profileRoot = join(modelRoot, profile.name)
    for (const provider of directories(profileRoot)) {
      const piRoot = join(profileRoot, provider.name, 'pi')
      if (!existsSync(piRoot)) continue
      candidates.push(join(piRoot, 'mcp.json'))
      for (const run of directories(join(piRoot, 'runs'))) {
        candidates.push(join(piRoot, 'runs', run.name, 'mcp.json'))
      }
      for (const room of directories(join(piRoot, 'group-chat'))) {
        for (const agent of directories(join(piRoot, 'group-chat', room.name))) {
          candidates.push(join(piRoot, 'group-chat', room.name, agent.name, 'mcp.json'))
        }
      }
    }
  }

  let migratedCount = 0
  for (const path of candidates) {
    const content = await safeReadFile(path)
    if (!content) continue
    const migrated = migratePiRuntimeMcpContent(content)
    if (!migrated) continue
    await writeFile(path, migrated, 'utf-8')
    migratedCount += 1
  }
  return migratedCount
}

function persistedPiRuntimeRoots(): string[] {
  const modelRoot = join(getWebUiHome(), CODING_AGENT_HOME_DIR, 'model')
  if (!existsSync(modelRoot)) return []
  const roots: string[] = []
  const directories = (path: string) => {
    try {
      return readdirSync(path, { withFileTypes: true }).filter(entry => entry.isDirectory())
    } catch {
      return []
    }
  }

  for (const profile of directories(modelRoot)) {
    const profileRoot = join(modelRoot, profile.name)
    for (const provider of directories(profileRoot)) {
      const piRoot = join(profileRoot, provider.name, 'pi')
      if (!existsSync(piRoot)) continue
      roots.push(piRoot)
      for (const run of directories(join(piRoot, 'runs'))) {
        roots.push(join(piRoot, 'runs', run.name))
      }
      for (const room of directories(join(piRoot, 'group-chat'))) {
        for (const agent of directories(join(piRoot, 'group-chat', room.name))) {
          roots.push(join(piRoot, 'group-chat', room.name, agent.name))
        }
      }
    }
  }
  return roots
}

export async function restorePersistedPiProxyTargets(): Promise<number> {
  let restoredCount = 0
  for (const root of persistedPiRuntimeRoots()) {
    const targetPath = join(root, PI_PROXY_TARGET_FILE)
    let content = await safeReadFile(targetPath)
    if (!content) {
      const modelsContent = await safeReadFile(join(root, 'models.json'))
      if (!modelsContent) continue
      try {
        const models = JSON.parse(modelsContent)
        const providerConfig = models?.providers?.[PI_PROVIDER_ID]
        const proxyBaseUrl = String(providerConfig?.baseUrl || '').trim()
        const token = String(providerConfig?.apiKey || '').trim()
        const routeKey = proxyBaseUrl.match(/\/api\/codex-proxy\/([^/]+)\/v1\/?$/)?.[1] || ''
        if (!routeKey || !token) continue
        const keyParts = JSON.parse(Buffer.from(routeKey, 'base64url').toString('utf-8'))
        if (!Array.isArray(keyParts) || keyParts.length < 5) continue
        const [profile, provider, model, apiMode, baseUrl, agentSessionId = '', chatSessionId = ''] = keyParts.map(value => String(value || ''))
        const resolved = await resolveStoredProviderLaunchInput({
          profile,
          provider,
          model,
          apiMode: normalizeLaunchApiMode(apiMode, 'chat_completions'),
          baseUrl,
          sessionId: chatSessionId,
        }, null)
        const apiKey = String(resolved.apiKey || '').trim()
        if (!apiKey) continue
        content = await serializePiProxyTarget({
          profile,
          provider,
          model,
          apiMode,
          baseUrl,
          agentId: 'pi',
          agentSessionId,
          chatSessionId,
        }, apiKey, token)
        await atomicWritePrivateFile(targetPath, content)
      } catch {
        continue
      }
    }
    try {
      const persisted = JSON.parse(content)
      const input = persisted?.input
      const token = String(persisted?.token || '').trim()
      const legacyApiKey = String(input?.apiKey || '').trim()
      if (!input || typeof input !== 'object' || !token) continue
      const encryptedVersion = Number(persisted?.apiKeyEncrypted?.v || 0)
      const apiKey = legacyApiKey || String(await decryptPiProxyApiKey(persisted?.apiKeyEncrypted, input, token) || '').trim()
      if (!String(input.profile || '').trim()
        || !String(input.provider || '').trim()
        || !String(input.model || '').trim()
        || !String(input.baseUrl || '').trim()
        || !apiKey) continue
      const restoredInput = { ...input, apiKey }
      delete restoredInput.apiKeyEncrypted
      restoreCodexProxyTarget(restoredInput, token)
      if (legacyApiKey || encryptedVersion === 1) {
        const migratedInput = { ...input }
        delete migratedInput.apiKey
        await atomicWritePrivateFile(
          targetPath,
          await serializePiProxyTarget(migratedInput, apiKey, token),
        )
      }
      await chmod(targetPath, 0o600)
      restoredCount += 1
    } catch {
      // Ignore invalid or legacy files; preparing the launch rewrites them.
    }
  }
  return restoredCount
}

function piModelsConfig(input: {
  baseUrl: string
  apiKey: string
  apiMode: ApiMode
  model: string
  profile: string
  provider: string
  reasoningEffort?: string
}): string {
  const capabilities = getModelRuntimeCapabilities(input)
  const reasoning = piModelSupportsThinking(capabilities.reasoning, input.reasoningEffort)
  return `${JSON.stringify({
    providers: {
      [PI_PROVIDER_ID]: {
        baseUrl: input.baseUrl,
        apiKey: input.apiKey,
        // The local Codex proxy exposes a Responses endpoint and performs the
        // selected upstream Chat Completions / Responses / Anthropic adaptation.
        api: 'openai-responses',
        models: [{
          id: input.model,
          name: displayNameForModel(input.model),
          reasoning,
          ...(reasoning ? { thinkingLevelMap: PI_EXTENDED_THINKING_LEVEL_MAP } : {}),
          input: capabilities.input,
          contextWindow: capabilities.contextWindow,
          maxTokens: capabilities.outputLimit,
          cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
        }],
      },
    },
  }, null, 2)}\n`
}

function piLiveConfigDefault(key: string, _profile: string): string | null {
  if (key === 'settings') return piSettingsConfig()
  if (key === 'mcp') return piUserMcpConfig()
  return null
}

async function ensurePiScopedBaseConfigFiles(scope: Required<CodingAgentConfigScope>): Promise<void> {
  const rootDir = getScopedConfigRoot('pi', scope)
  await mkdir(rootDir, { recursive: true })

  for (const definition of CONFIG_FILE_DEFINITIONS.pi) {
    const content = piLiveConfigDefault(definition.key, scope.profile)
    if (content == null) continue
    const absolutePath = join(rootDir, definition.scopedPath)
    if (existsSync(absolutePath)) continue
    await writeFile(absolutePath, content, { encoding: 'utf-8', flag: 'wx' }).catch((err: any) => {
      if (err?.code !== 'EEXIST') throw err
    })
  }
}

function buildLaunchShellCommand(input: {
  workspaceDir: string
  env: Record<string, string>
  command: string
  args: string[]
}): string {
  if (process.platform === 'win32') {
    const envAssignments = Object.entries(input.env)
      .map(([key, value]) => `$env:${key} = ${powerShellQuote(value)}`)
    return [
      `Set-Location -LiteralPath ${powerShellQuote(input.workspaceDir)}`,
      ...envAssignments,
      `& ${powerShellQuote(input.command)} ${input.args.map(powerShellQuote).join(' ')}`.trim(),
    ].join('; ')
  }

  const envPrefix = Object.entries(input.env).map(([key, value]) => `${key}=${shellQuote(value)}`).join(' ')
  const runCommand = [
    envPrefix,
    shellQuote(input.command),
    ...input.args.map(shellQuote),
  ].filter(Boolean).join(' ')
  return `cd ${shellQuote(input.workspaceDir)} && ${runCommand}`
}

function buildPosixLauncherScript(input: {
  workspaceDir: string
  env: Record<string, string>
  command: string
  args: string[]
}): string {
  const exports = Object.entries(input.env)
    .map(([key, value]) => `export ${key}=${shellQuote(value)}`)
  const command = [
    shellQuote(input.command),
    ...input.args.map(shellQuote),
  ].join(' ')
  return [
    '#!/usr/bin/env bash',
    'set -e',
    `cd ${shellQuote(input.workspaceDir)}`,
    ...exports,
    `exec ${command}`,
    '',
  ].join('\n')
}

function buildPowerShellLauncherScript(input: {
  workspaceDir: string
  env: Record<string, string>
  command: string
  args: string[]
}): string {
  const envAssignments = Object.entries(input.env)
    .map(([key, value]) => `$env:${key} = ${powerShellQuote(value)}`)
  const command = [
    `& ${powerShellQuote(input.command)}`,
    ...input.args.map(powerShellQuote),
  ].join(' ')
  return [
    '$ErrorActionPreference = "Stop"',
    `Set-Location -LiteralPath ${powerShellQuote(input.workspaceDir)}`,
    ...envAssignments,
    command,
    'exit $LASTEXITCODE',
    '',
  ].join('\r\n')
}

async function writeLauncherScript(input: {
  rootDir: string
  workspaceDir: string
  env: Record<string, string>
  command: string
  args: string[]
}): Promise<string> {
  const isWindows = process.platform === 'win32'
  const launcherPath = join(input.rootDir, isWindows ? WINDOWS_LAUNCHER_FILE : POSIX_LAUNCHER_FILE)
  await writeFile(
    launcherPath,
    isWindows ? buildPowerShellLauncherScript(input) : buildPosixLauncherScript(input),
    'utf-8',
  )
  if (!isWindows) await chmod(launcherPath, 0o700)
  return launcherPath
}

function buildLauncherShellCommand(workspaceDir: string, launcherPath: string): string {
  return process.platform === 'win32'
    ? buildLaunchShellCommand({
        workspaceDir,
        env: {},
        command: 'powershell.exe',
        args: ['-NoProfile', '-ExecutionPolicy', 'Bypass', '-File', launcherPath],
      })
    : buildLaunchShellCommand({
        workspaceDir,
        env: {},
        command: launcherPath,
        args: [],
      })
}

function appleScriptString(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

async function commandExists(command: string): Promise<boolean> {
  try {
    await execFileAsync(process.platform === 'win32' ? 'where' : 'which', [command], {
      encoding: 'utf-8',
      timeout: 3000,
      windowsHide: true,
    })
    return true
  } catch {
    return false
  }
}

function isDockerRuntime(): boolean {
  return existsSync('/.dockerenv') || process.env.container === 'docker'
}

async function openNativeTerminal(shellCommand: string): Promise<string> {
  if (process.platform === 'win32') {
    const escapedCommand = shellCommand.replace(/"/g, '""').replace(/\$/g, '`$')
    await execFileAsync('powershell.exe', [
      '-NoProfile',
      '-Command',
      `Start-Process -FilePath powershell.exe -ArgumentList @('-NoExit', '-Command', "${escapedCommand}")`,
    ], {
      encoding: 'utf-8',
      timeout: 8000,
      windowsHide: true,
    })
    return 'PowerShell'
  }

  if (process.platform === 'darwin') {
    await execFileAsync('osascript', [
      '-e',
      `tell application "Terminal" to do script ${appleScriptString(shellCommand)}`,
      '-e',
      'tell application "Terminal" to activate',
    ], {
      encoding: 'utf-8',
      timeout: 8000,
      windowsHide: true,
    })
    return 'Terminal.app'
  }

  if (process.platform === 'linux') {
    if (isDockerRuntime()) {
      const err = new Error('Native terminal is not available inside Docker')
      ;(err as any).status = 400
      throw err
    }
    if (!process.env.DISPLAY && !process.env.WAYLAND_DISPLAY) {
      const err = new Error('Native terminal requires a Linux desktop session')
      ;(err as any).status = 400
      throw err
    }

    const candidates: Array<{ command: string; args: string[] }> = [
      { command: 'xdg-terminal-exec', args: ['bash', '-lc', shellCommand] },
      { command: 'gnome-terminal', args: ['--', 'bash', '-lc', shellCommand] },
      { command: 'konsole', args: ['-e', 'bash', '-lc', shellCommand] },
      { command: 'xfce4-terminal', args: ['--command', `bash -lc ${shellQuote(shellCommand)}`] },
      { command: 'kitty', args: ['bash', '-lc', shellCommand] },
      { command: 'alacritty', args: ['-e', 'bash', '-lc', shellCommand] },
      { command: 'xterm', args: ['-e', 'bash', '-lc', shellCommand] },
    ]

    const errors: string[] = []
    for (const candidate of candidates) {
      if (!(await commandExists(candidate.command))) continue
      try {
        await execFileAsync(candidate.command, candidate.args, {
          encoding: 'utf-8',
          timeout: 8000,
          windowsHide: true,
        })
        return candidate.command
      } catch (err: any) {
        errors.push(`${candidate.command}: ${normalizeError(err)}`)
      }
    }

    const err = new Error(errors[0] || 'No supported Linux terminal command was found')
    ;(err as any).status = 400
    throw err
  }

  const err = new Error('Native terminal launch is not supported on this platform')
  ;(err as any).status = 400
  throw err
}

function getLiveConfigFileDefinition(id: string, key: string): CodingAgentConfigFileDefinition | null {
  const tool = getCodingAgentDefinition(id)
  if (!tool) return null
  const definition = CONFIG_FILE_DEFINITIONS[tool.id].find(file => file.key === key)
  if (!definition) return null
  return {
    key: definition.key,
    path: definition.path,
    language: definition.language,
    absolutePath: expandHomePath(definition.path),
  }
}

function getScopedConfigFileDefinition(
  id: string,
  key: string,
  scopeInput: CodingAgentConfigScope = {},
  rootDirOverride?: string,
): (CodingAgentConfigFileDefinition & Required<CodingAgentConfigScope> & { rootDir: string }) | null {
  const tool = getCodingAgentDefinition(id)
  if (!tool) return null
  const definition = CONFIG_FILE_DEFINITIONS[tool.id].find(file => file.key === key)
  if (!definition) return null
  const scope = normalizeConfigScope(scopeInput)
  const rootDir = rootDirOverride || getScopedConfigRoot(tool.id, scope)
  return {
    key: definition.key,
    path: definition.path,
    language: definition.language,
    ...scope,
    rootDir,
    absolutePath: join(rootDir, definition.scopedPath),
  }
}

function getCurrentNodeEnv(): NodeJS.ProcessEnv {
  return {
    ...process.env,
    PATH: [getNodeBinDir(), getNvmNodeBinPaths(), process.env.PATH].filter(Boolean).join(delimiter),
    npm_node_execpath: process.execPath,
  }
}

async function npmExecution(args: string[], env: NodeJS.ProcessEnv): Promise<CommandExecution> {
  const bundledNpmCli = getNpmCliPath()
  if (bundledNpmCli) return { command: process.execPath, args: [bundledNpmCli, ...args] }

  let npmBin: string | null = null
  for (const command of [...new Set([getNpmBin(), 'npm'])]) {
    const paths = await findCommandPaths(command, env)
    if (paths[0]) {
      npmBin = paths[0]
      break
    }
  }
  if (!npmBin) throw nodeEnvironmentMissingError()

  const npmCli = npmCliFromNpmBin(npmBin)
  if (npmCli) return { command: npmCli.node, args: [npmCli.npmCli, ...args] }

  let nodeBin: string | null = null
  for (const command of [...new Set([process.platform === 'win32' ? 'node.exe' : 'node', 'node'])]) {
    const paths = await findCommandPaths(command, env)
    if (paths[0]) {
      nodeBin = paths[0]
      break
    }
  }
  if (!nodeBin) throw nodeEnvironmentMissingError()

  return commandExecution(npmBin, args)
}

async function runNpm(args: string[], options: { timeout?: number; env?: NodeJS.ProcessEnv } = {}) {
  const env = {
    ...getCurrentNodeEnv(),
    ...options.env,
  }
  const execution = await npmExecution(args, env)
  return execFileAsync(execution.command, execution.args, {
    encoding: 'utf-8',
    timeout: options.timeout,
    windowsHide: true,
    windowsVerbatimArguments: execution.windowsVerbatimArguments,
    maxBuffer: 10 * 1024 * 1024,
    env,
  })
}

function normalizeError(err: any): string {
  if (isNodeEnvironmentMissingError(err)) return nodeEnvironmentMissingError().message
  const stderr = typeof err?.stderr === 'string' ? err.stderr.trim() : ''
  const stdout = typeof err?.stdout === 'string' ? err.stdout.trim() : ''
  const message = stderr || stdout || err?.message || String(err)
  return message.split(/\r?\n/).filter(Boolean).slice(0, 4).join('\n')
}

function normalizeErrorCode(err: any): string | undefined {
  return isNodeEnvironmentMissingError(err) ? NODE_ENVIRONMENT_MISSING_CODE : undefined
}

async function findCommandPaths(command: string, env: NodeJS.ProcessEnv): Promise<string[]> {
  try {
    const lookupCommand = process.platform === 'win32' ? 'where' : 'which'
    const lookupArgs = process.platform === 'win32' ? [command] : ['-a', command]
    const { stdout } = await execFileAsync(lookupCommand, lookupArgs, {
      encoding: 'utf-8',
      timeout: 5000,
      windowsHide: true,
      env,
    })
    return stdout.split(/\r?\n/).map(line => normalizeWindowsCommandPath(line.trim())).filter(Boolean)
  } catch {
    return []
  }
}

async function resolveCommandForExecution(command: string, env: NodeJS.ProcessEnv): Promise<string> {
  if (process.platform !== 'win32') return command
  const paths = await findCommandPaths(command, env)
  // On Windows, prioritize paths with .cmd or .bat extensions since where may return
  // both the unix-style script (without extension) and the Windows shim (.cmd)
  const windowsPath = paths.find(path => windowsCommandNeedsShell(path))
  return windowsPath || paths[0] || command
}

function commandExecution(command: string, args: string[]): CommandExecution {
  const normalizedCommand = normalizeWindowsCommandPath(command)
  if (process.platform === 'win32' && windowsCommandNeedsShell(normalizedCommand)) {
    return windowsCmdShimExecution(normalizedCommand, args)
  }
  return { command: normalizedCommand, args }
}

function packageParts(packageName: string): string[] {
  return packageName.split('/').filter(Boolean)
}

function getPrefixFromPackagePath(path: string, packageName: string): string | null {
  const normalized = path.replace(/\\/g, '/')
  const parts = normalized.split('/').filter(Boolean)
  const nodeModulesIndex = parts.lastIndexOf('node_modules')
  const packageNameParts = packageParts(packageName)

  if (nodeModulesIndex <= 0) return null
  for (let i = 0; i < packageNameParts.length; i += 1) {
    if (parts[nodeModulesIndex + 1 + i] !== packageNameParts[i]) return null
  }

  const libIndex = nodeModulesIndex - 1
  if (parts[libIndex] !== 'lib') return null
  const prefixParts = parts.slice(0, libIndex)
  if (prefixParts.length === 0) return process.platform === 'win32' ? null : '/'
  return `${normalized.startsWith('/') ? '/' : ''}${prefixParts.join('/')}`
}

async function getCommandPackagePrefixes(definition: CodingAgentDefinition, env: NodeJS.ProcessEnv): Promise<string[]> {
  const commandPaths = await findCommandPaths(definition.command, env)
  const prefixes = new Set<string>()

  for (const commandPath of commandPaths) {
    const candidates = [commandPath]
    try {
      candidates.push(realpathSync(commandPath))
    } catch {
      // Keep the unresolved command path as the fallback candidate.
    }

    for (const candidate of candidates) {
      const prefix = getPrefixFromPackagePath(candidate, definition.packageName)
      if (prefix) prefixes.add(prefix)
    }
  }
  return [...prefixes]
}

function extractVersion(raw: string): string {
  const trimmed = raw.trim()
  return trimmed.match(/\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?/)?.[0] || trimmed.split(/\s+/)[0] || ''
}

async function getGlobalNpmBin(): Promise<string | null> {
  if (typeof cachedGlobalNpmBin !== 'undefined') return cachedGlobalNpmBin
  try {
    const { stdout } = await runNpm(['prefix', '-g'], { timeout: 5000 })
    const prefix = stdout.trim()
    cachedGlobalNpmBin = prefix ? (process.platform === 'win32' ? prefix : join(prefix, 'bin')) : null
  } catch {
    cachedGlobalNpmBin = null
  }
  return cachedGlobalNpmBin
}

async function commandEnv(): Promise<NodeJS.ProcessEnv> {
  const env = getCurrentNodeEnv()
  const npmBin = await getGlobalNpmBin()
  const loginShellPath = await getLoginShellPath()
  prependPathEntries(env, [
    npmBin,
    loginShellPath,
    ...getDesktopCommonBinPaths(),
  ])
  return env
}

/** Product-managed DeepSeek Harness SDK runtime tree under the Web UI home. */
export function getDshSdkDir(): string {
  return join(getWebUiHome(), 'dsh-sdk')
}

/**
 * Cordis component packages referenced by {@link DSH_SDK_CORDIS_TEMPLATE}.
 * Deriving the list from the template keeps the one-click SDK install in sync
 * with the composition the runtime actually boots.
 */
export function dshSdkTemplatePackages(): string[] {
  const names = new Set<string>()
  for (const line of DSH_SDK_CORDIS_TEMPLATE.split('\n')) {
    const match = line.match(/^  name: '(@deepseek-ai\/[^']+)'$/)
    if (match) names.add(match[1])
  }
  return [...names].sort()
}

/**
 * npm specs for one coherent DeepSeek Harness SDK runtime install: the
 * `dsh-jsonrpc-agent` bin package plus every cordis component from the
 * template, all pinned to {@link DSH_SDK_VERSION}.
 */
export function getDshSdkInstallSpecs(version = DSH_SDK_VERSION): string[] {
  return [DSH_SDK_RUNTIME_PACKAGE, ...dshSdkTemplatePackages()].map(pkg => `${pkg}@${version}`)
}

/** Locate the `dsh-jsonrpc-agent` bin inside the product-managed SDK tree, if any. */
function resolveDshSdkDirBin(): string | null {
  const binDir = join(getDshSdkDir(), 'node_modules', '.bin')
  const candidates = process.platform === 'win32'
    ? [join(binDir, 'dsh-jsonrpc-agent.cmd'), join(binDir, 'dsh-jsonrpc-agent')]
    : [join(binDir, 'dsh-jsonrpc-agent')]
  return candidates.find(candidate => existsSync(candidate)) || null
}

/**
 * Install the DeepSeek Harness SDK runtime into the product-managed tree when
 * it is not already resolvable (PATH bin, source checkout, or managed tree).
 * Returns null on success or when the SDK is already available, otherwise an
 * error message. Best-effort: a failure keeps the headless basic mode working.
 */
async function ensureDshSdkInstalled(env: NodeJS.ProcessEnv): Promise<string | null> {
  try {
    if (await resolveDshSdkLaunch()) return null
    const sdkDir = getDshSdkDir()
    await runNpm(
      ['install', '--prefix', sdkDir, '--no-audit', '--no-fund', ...getDshSdkInstallSpecs()],
      { timeout: 10 * 60 * 1000, env },
    )
    return (await resolveDshSdkLaunch()) ? null : 'SDK packages installed but the dsh-jsonrpc-agent runtime was not found'
  } catch (err) {
    return normalizeError(err)
  }
}

/** Discover a local DeepSeek Harness source checkout, if present. */
function findDshSdkCheckout(): string | null {
  const candidates = [
    process.env.DEEPSEEK_HARNESS_ROOT,
    join(homedir(), 'dev', 'deepseek-harness'),
    join(homedir(), 'deepseek-harness'),
    '/home/kali/dev/deepseek-harness',
  ].filter((value): value is string => typeof value === 'string' && value.trim() !== '')
  for (const root of candidates) {
    const hasBin = existsSync(join(root, DSH_SDK_SRC_BIN))
    const hasConfig = existsSync(join(root, DSH_SDK_SRC_CONFIG))
    const hasTsx = existsSync(join(root, 'node_modules', 'tsx')) || existsSync(join(root, 'node_modules', '.bin', 'tsx'))
    if (hasBin && hasConfig && hasTsx) return root
  }
  return null
}

/**
 * Resolve how to launch the DeepSeek Harness SDK runtime for the full
 * streaming capability: prefer a `dsh-jsonrpc-agent` binary on PATH, then the
 * product-managed SDK tree installed by the one-click install, then a local
 * source checkout driven by `node --import tsx`. Returns null when the SDK
 * runtime is not installed, in which case DeepSeek Harness falls back to the
 * headless CLI single-turn mode.
 */
async function resolveDshSdkLaunch(): Promise<{ command: string; args: string[]; runtimeCwd?: string } | null> {
  try {
    const env = await commandEnv()
    const paths = await findCommandPaths(DSH_SDK_BIN, env)
    const resolved = paths[0] ? await resolveCommandForExecution(DSH_SDK_BIN, env) : null
    if (resolved) return { command: resolved, args: [] }
  } catch {
    // Fall through to the product-managed SDK tree.
  }
  const managedBin = resolveDshSdkDirBin()
  if (managedBin) return { command: managedBin, args: [] }
  const checkout = findDshSdkCheckout()
  if (checkout) {
    return {
      command: process.execPath,
      args: ['--import', 'tsx', join(checkout, DSH_SDK_SRC_BIN), join(checkout, DSH_SDK_SRC_CONFIG)],
      runtimeCwd: checkout,
    }
  }
  return null
}

export function getCodingAgentDefinition(id: string): CodingAgentDefinition | null {
  return TOOL_DEFINITIONS.find(tool => tool.id === id) || null
}

export function getCodingAgentConfigFileDefinitions(id: string): CodingAgentConfigFileDefinition[] {
  const tool = getCodingAgentDefinition(id)
  if (!tool) return []
  return CONFIG_FILE_DEFINITIONS[tool.id].map(file => ({
    key: file.key,
    path: file.path,
    language: file.language,
    absolutePath: expandHomePath(file.path),
  }))
}

export async function getCodingAgentStatus(definition: CodingAgentDefinition): Promise<CodingAgentToolStatus> {
  try {
    const env = await commandEnv()
    const resolvedCommand = await resolveCommandForExecution(definition.command, env)
    const execution = commandExecution(resolvedCommand, ['--version'])
    const { stdout, stderr } = await execFileAsync(execution.command, execution.args, {
      encoding: 'utf-8',
      timeout: 8000,
      windowsHide: true,
      windowsVerbatimArguments: execution.windowsVerbatimArguments,
      env,
    })
    const rawVersion = `${stdout || ''}${stderr || ''}`.trim()
    if (definition.id === 'pi' && !existsSync(getPiMcpAdapterEntry())) {
      return {
        ...definition,
        installed: false,
        version: extractVersion(rawVersion),
        rawVersion,
        error: `Pi MCP Adapter ${PI_MCP_ADAPTER_VERSION} is not installed`,
      }
    }
    return {
      ...definition,
      installed: true,
      version: extractVersion(rawVersion),
      rawVersion,
      ...(definition.id === 'dsh' ? { sdkAvailable: Boolean(await resolveDshSdkLaunch()) } : {}),
    }
  } catch (err: any) {
    return {
      ...definition,
      installed: false,
      version: '',
      rawVersion: '',
      error: normalizeError(err),
      ...(definition.id === 'dsh' ? { sdkAvailable: Boolean(await resolveDshSdkLaunch()) } : {}),
    }
  }
}

export async function getCodingAgentsStatus(): Promise<CodingAgentsStatus> {
  return {
    tools: await Promise.all(TOOL_DEFINITIONS.map(tool => getCodingAgentStatus(tool))),
  }
}

export interface CodingAgentUpdateResult {
  success: boolean
  tool: CodingAgentToolStatus
  latestVersion: string
  updateAvailable: boolean
  message?: string
}

function versionGte(a: string, b: string): boolean {
  const x = String(a).match(/\d+(?:\.\d+){0,2}/)
  const y = String(b).match(/\d+(?:\.\d+){0,2}/)
  if (!x || !y) return String(a) === String(b)
  const p = x[0].split('.').map(Number)
  const q = y[0].split('.').map(Number)
  for (let i = 0; i < 3; i += 1) {
    const u = p[i] || 0
    const v = q[i] || 0
    if (u !== v) return u > v
  }
  return true
}

export function codexToolSearchConfig(version: string): { toolSearch: boolean; alwaysDefer: boolean } {
  if (!String(version || '').trim()) {
    // Keep the pre-gating behavior when the CLI version cannot be resolved.
    return { toolSearch: true, alwaysDefer: true }
  }
  const toolSearch = versionGte(version, CODEX_TOOL_SEARCH_MIN_VERSION)
  return {
    toolSearch,
    alwaysDefer: toolSearch && !versionGte(version, CODEX_TOOL_SEARCH_ALWAYS_DEFER_REMOVED_VERSION),
  }
}

async function resolveCodexToolSearchConfig(): Promise<{ toolSearch: boolean; alwaysDefer: boolean }> {
  if (process.env.VITEST) return { toolSearch: true, alwaysDefer: true }
  if (cachedCodexVersion && Date.now() - cachedCodexVersion.checkedAt < CODEX_VERSION_CACHE_TTL_MS) {
    return codexToolSearchConfig(cachedCodexVersion.version)
  }
  const definition = getCodingAgentDefinition('codex')
  if (!definition) return { toolSearch: true, alwaysDefer: true }
  const status = await getCodingAgentStatus(definition)
  cachedCodexVersion = { version: status.version, checkedAt: Date.now() }
  return codexToolSearchConfig(status.version)
}

export async function checkUpdateAgent(id: string): Promise<CodingAgentUpdateResult> {
  const tool = getCodingAgentDefinition(id)
  if (!tool) {
    const err = new Error('Unknown coding agent')
    ;(err as any).status = 400
    throw err
  }
  try {
    if (tool.id === 'pi') {
      const status = await getCodingAgentStatus(tool)
      const latestVersion = PI_CODING_AGENT_VERSION
      const updateAvailable = status.installed && !versionGte(status.version, latestVersion)
      return { success: true, tool: status, latestVersion, updateAvailable }
    }
    const env = await commandEnv()
    const { stdout } = await runNpm(['view', tool.packageName, 'version'], { timeout: 15_000, env })
    const latestVersion = stdout.trim()
    const status = await getCodingAgentStatus(tool)
    const updateAvailable = !!latestVersion && status.installed && !versionGte(status.version, latestVersion)
    return { success: true, tool: status, latestVersion, updateAvailable }
  } catch (err: any) {
    const status = await getCodingAgentStatus(tool)
    return { success: false, tool: status, latestVersion: '', updateAvailable: false, message: normalizeError(err) }
  }
}

export async function installCodingAgent(id: string): Promise<CodingAgentMutationResult> {
  const tool = getCodingAgentDefinition(id)
  if (!tool) {
    const err = new Error('Unknown coding agent')
    ;(err as any).status = 400
    throw err
  }
  if (installingTools.has(tool.id)) {
    const err = new Error('Install is already running')
    ;(err as any).status = 409
    throw err
  }

  installingTools.add(tool.id)
  try {
    const env = await commandEnv()
    await runNpm(['install', '-g', tool.id === 'pi' ? PI_CODING_AGENT_PACKAGE : tool.packageName], {
      timeout: 10 * 60 * 1000,
      env,
    })
    if (tool.id === 'pi') {
      const adapterRoot = getPiMcpAdapterRoot()
      await mkdir(adapterRoot, { recursive: true })
      await runNpm(['install', '--prefix', adapterRoot, '--save-exact', PI_MCP_ADAPTER_PACKAGE], {
        timeout: 10 * 60 * 1000,
        env,
      })
    }
    cachedGlobalNpmBin = undefined
    // DeepSeek Harness ships its full streaming SDK runtime as separate npm
    // packages; install it into the product-managed tree so scoped launches
    // run in SDK mode instead of the headless single-turn fallback.
    const sdkMessage = tool.id === 'dsh' ? await ensureDshSdkInstalled(env) : null
    const status = await getCodingAgentStatus(tool)
    const allStatus = await getCodingAgentsStatus()
    return {
      success: status.installed,
      tool: status,
      tools: allStatus.tools,
      message: status.installed
        ? sdkMessage
          ? `Installed (${sdkMessage})`
          : 'Installed'
        : status.error || 'Install completed but the command was not found',
    }
  } catch (err: any) {
    const status = await getCodingAgentStatus(tool)
    const allStatus = await getCodingAgentsStatus()
    return {
      success: false,
      tool: status,
      tools: allStatus.tools,
      message: normalizeError(err),
      code: normalizeErrorCode(err),
    }
  } finally {
    installingTools.delete(tool.id)
  }
}

export async function deleteCodingAgent(id: string): Promise<CodingAgentMutationResult> {
  const tool = getCodingAgentDefinition(id)
  if (!tool) {
    const err = new Error('Unknown coding agent')
    ;(err as any).status = 400
    throw err
  }
  if (deletingTools.has(tool.id)) {
    const err = new Error('Delete is already running')
    ;(err as any).status = 409
    throw err
  }

  deletingTools.add(tool.id)
  try {
    const env = await commandEnv()
    const packagePrefixes = await getCommandPackagePrefixes(tool, env)
    const uninstallArgsList = packagePrefixes.length > 0
      ? packagePrefixes.map(prefix => ['uninstall', '-g', '--prefix', prefix, tool.packageName])
      : [['uninstall', '-g', tool.packageName]]
    for (const uninstallArgs of uninstallArgsList) {
      await runNpm(uninstallArgs, {
        timeout: 10 * 60 * 1000,
        env,
      })
    }
    if (tool.id === 'pi') {
      await runNpm(['uninstall', '--prefix', getPiMcpAdapterRoot(), 'pi-mcp-adapter'], {
        timeout: 10 * 60 * 1000,
        env,
      })
    }
    codingAgentRunManager.stopMatching(launch => launch.agentId === tool.id, { reportClosed: true })
    cachedGlobalNpmBin = undefined
    // Remove the product-managed DeepSeek Harness SDK runtime tree too.
    if (tool.id === 'dsh') {
      await rm(getDshSdkDir(), { recursive: true, force: true })
    }
    const status = await getCodingAgentStatus(tool)
    const allStatus = await getCodingAgentsStatus()
    return {
      success: !status.installed,
      tool: status,
      tools: allStatus.tools,
      message: !status.installed ? 'Deleted' : 'Delete completed but the command is still available',
    }
  } catch (err: any) {
    const status = await getCodingAgentStatus(tool)
    const allStatus = await getCodingAgentsStatus()
    return {
      success: false,
      tool: status,
      tools: allStatus.tools,
      message: normalizeError(err),
      code: normalizeErrorCode(err),
    }
  } finally {
    deletingTools.delete(tool.id)
  }
}

export async function readCodingAgentConfigFile(id: string, key: string, scope: CodingAgentConfigScope = {}): Promise<CodingAgentConfigFileContent> {
  const definition = getLiveConfigFileDefinition(id, key)
  if (!definition) {
    const err = new Error('Unknown coding agent config file')
    ;(err as any).status = 404
    throw err
  }
  const normalizedScope = normalizeConfigScope(scope)

  try {
    const info = await stat(definition.absolutePath)
    if (!info.isFile()) {
      const err = new Error('Config path is not a file')
      ;(err as any).status = 400
      throw err
    }
    if (info.size > MAX_CONFIG_FILE_SIZE) {
      const err = new Error('Config file is too large to edit')
      ;(err as any).status = 413
      throw err
    }
    const content = await readFile(definition.absolutePath, 'utf-8')
    return {
      ...definition,
      ...normalizedScope,
      rootDir: dirname(definition.absolutePath),
      content,
      exists: true,
      size: info.size,
    }
  } catch (err: any) {
    if (err?.code !== 'ENOENT') throw err
    const defaultContent = id === 'pi'
      ? piLiveConfigDefault(key, normalizedScope.profile) || ''
      : ''
    return {
      ...definition,
      ...normalizedScope,
      rootDir: dirname(definition.absolutePath),
      content: defaultContent,
      exists: false,
      size: Buffer.byteLength(defaultContent),
    }
  }
}

export async function writeCodingAgentConfigFile(id: string, key: string, content: string, scope: CodingAgentConfigScope = {}): Promise<CodingAgentConfigFileContent> {
  const definition = getLiveConfigFileDefinition(id, key)
  if (!definition) {
    const err = new Error('Unknown coding agent config file')
    ;(err as any).status = 404
    throw err
  }
  const normalizedScope = normalizeConfigScope(scope)

  const buffer = Buffer.from(content || '', 'utf-8')
  if (buffer.length > MAX_CONFIG_FILE_SIZE) {
    const err = new Error('Config file content is too large')
    ;(err as any).status = 413
    throw err
  }

  await mkdir(dirname(definition.absolutePath), { recursive: true })
  await writeFile(definition.absolutePath, buffer)
  codingAgentRunManager.stopMatching(launch => (
    launch.agentId === id &&
    launch.profile === normalizedScope.profile &&
    normalizeScopeSegment(launch.provider, 'default', 'provider') === normalizedScope.provider
  ), { reportClosed: true })
  return {
    ...definition,
    ...normalizedScope,
    rootDir: dirname(definition.absolutePath),
    content,
    exists: true,
    size: buffer.length,
  }
}

export async function prepareCodingAgentLaunch(id: string, input: CodingAgentLaunchInput): Promise<CodingAgentLaunchResult> {
  const tool = getCodingAgentDefinition(id)
  if (!tool) {
    const err = new Error('Unknown coding agent')
    ;(err as any).status = 400
    throw err
  }

  const mode = input.mode === 'global' ? 'global' : 'scoped'
  if (mode === 'global') {
    const scope = normalizeConfigScope({ profile: input.profile, provider: 'global' })
    const workspaceDir = resolveLaunchWorkspaceRoot(scope, input.workspace)
    await mkdir(workspaceDir, { recursive: true })
    if (tool.id === 'pi' && input.piOutputMode === 'rpc') {
      const rootDir = getScopedRuntimeConfigRoot(tool.id, scope, input)
      const dynamicPromptPath = join(rootDir, PI_DYNAMIC_PROMPT_FILE)
      const studioExtensionPath = join(rootDir, PI_STUDIO_EXTENSION_FILE)
      await mkdir(rootDir, { recursive: true })
      await writeFile(studioExtensionPath, piStudioRuntimeExtension(), 'utf-8')
      await writeFile(dynamicPromptPath, '', 'utf-8')
      const files = [
        { key: 'studio_extension', path: PI_STUDIO_EXTENSION_FILE, absolutePath: studioExtensionPath },
        { key: 'dynamic_prompt', path: PI_DYNAMIC_PROMPT_FILE, absolutePath: dynamicPromptPath },
      ]
      const env = {
        PI_CODING_AGENT_DIR: join(getGlobalConfigHome(), '.pi', 'agent'),
        HERMES_PI_DYNAMIC_PROMPT_FILE: dynamicPromptPath,
      }
      const args = [
        '--mode', 'rpc',
        ...(input.agentNativeSessionId ? ['--session-id', input.agentNativeSessionId] : []),
        '--extension', studioExtensionPath,
        input.approveProjectConfig === true ? '--approve' : '--no-approve',
      ]
      const launcherPath = await writeLauncherScript({
        rootDir,
        workspaceDir,
        env,
        command: tool.command,
        args,
      })
      files.push({
        key: 'launcher',
        path: process.platform === 'win32' ? WINDOWS_LAUNCHER_FILE : POSIX_LAUNCHER_FILE,
        absolutePath: launcherPath,
      })
      return {
        agentId: tool.id,
        mode,
        profile: scope.profile,
        provider: scope.provider,
        model: '',
        rootDir,
        workspaceDir,
        command: tool.command,
        args,
        env,
        shellCommand: buildLauncherShellCommand(workspaceDir, launcherPath),
        files,
        reasoningEffort: String(input.reasoningEffort || '').trim(),
      }
    }
    const files = await ensureGlobalCodingAgentPromptFile(tool.id)
    const promptFile = files.find(file => file.key === 'prompt')?.absolutePath || ''
    const args = tool.id === 'claude-code'
      ? [
          ...(promptFile ? ['--append-system-prompt-file', promptFile] : []),
          ...claudeCodePermissionArgs(),
        ]
      : []
    const shellCommand = buildLaunchShellCommand({
      workspaceDir,
      env: {},
      command: tool.command,
      args,
    })
    return {
      agentId: tool.id,
      mode,
      profile: scope.profile,
      provider: scope.provider,
      model: '',
      rootDir: workspaceDir,
      workspaceDir,
      command: tool.command,
      args,
      env: {},
      shellCommand,
      files,
    }
  }

  const provider = normalizeProviderIdentity(input.provider)
  const scope = normalizeConfigScope({ profile: input.profile, provider })
  const model = String(input.model || '').trim()
  const apiKey = String(input.apiKey || '').trim()
  assertScopedCodingAgentProviderAllowed(mode, provider)
  if (!model) {
    const err = new Error('Model is required')
    ;(err as any).status = 400
    throw err
  }

  const baseUrl = String(input.baseUrl || '').trim()
  const preset = PROVIDER_PRESETS.find(item => item.value === provider)
  const apiMode = normalizeLaunchApiMode(input.apiMode, preset?.api_mode || 'chat_completions')
  const reasoningEffort = String(input.reasoningEffort || '').trim()
  const groupSystemPrompt = String(input.groupSystemPrompt || '').trim()
  const scopedSystemPrompt = tool.id === 'pi' && groupSystemPrompt ? getSystemPrompt() : groupSystemPrompt || getSystemPrompt()
  const isolatedInput = tool.id === 'pi'
    ? {
        ...input,
        sessionId: input.sessionId || randomUUID(),
        agentSessionId: input.agentSessionId || randomUUID(),
      }
    : input
  const rootDir = getScopedRuntimeConfigRoot(tool.id, scope, isolatedInput)
  const workspaceDir = resolveLaunchWorkspaceRoot(scope, input.workspace)
  await mkdir(rootDir, { recursive: true })
  await mkdir(workspaceDir, { recursive: true })

  const files: Array<{ key: string; path: string; absolutePath: string }> = []
  const writeScopedFile = async (key: string, content: string) => {
    const definition = getScopedConfigFileDefinition(tool.id, key, scope, rootDir)
    if (!definition) return
    await mkdir(dirname(definition.absolutePath), { recursive: true })
    await writeFile(definition.absolutePath, content, 'utf-8')
    files.push({ key, path: definition.path, absolutePath: definition.absolutePath })
  }
  const writeRuntimeFile = async (key: string, path: string, content: string) => {
    const absolutePath = join(rootDir, path)
    await mkdir(dirname(absolutePath), { recursive: true })
    await writeFile(absolutePath, content, 'utf-8')
    files.push({ key, path, absolutePath })
  }

  let command = tool.command
  let runtimeCwd: string | undefined
  let args: string[] = []
  let env: Record<string, string> = {}

  if (tool.id === 'claude-code') {
    const contextWindow = getModelContextLength({ profile: scope.profile, provider, model })
    const proxyTarget = baseUrl && apiKey
      ? registerClaudeCodeProxyTarget({
          provider,
          model,
          baseUrl,
          apiKey,
          apiMode,
          reasoningEffort,
          agentId: tool.id,
          agentSessionId: isolatedInput.agentSessionId,
          chatSessionId: isolatedInput.sessionId,
        })
      : null
    const claudeBaseUrl = proxyTarget?.baseUrl || baseUrl
    const claudeApiKey = proxyTarget?.token || apiKey
    const modelName = displayNameForModel(model)
    const globalSettingsPath = getLiveConfigFileDefinition(tool.id, 'settings')?.absolutePath
    const inheritedSettings = inheritClaudeSettings(globalSettingsPath ? await safeReadFile(globalSettingsPath) : '')
    const settings = {
      ...inheritedSettings,
      model,
      env: {
        ...(claudeApiKey ? { ANTHROPIC_API_KEY: claudeApiKey } : {}),
        ...(claudeBaseUrl ? { ANTHROPIC_BASE_URL: claudeBaseUrl } : {}),
        ANTHROPIC_MODEL: model,
        ANTHROPIC_CUSTOM_MODEL_OPTION: model,
        ANTHROPIC_CUSTOM_MODEL_OPTION_NAME: modelName,
        ANTHROPIC_DEFAULT_HAIKU_MODEL: model,
        ANTHROPIC_DEFAULT_HAIKU_MODEL_NAME: modelName,
        ANTHROPIC_DEFAULT_SONNET_MODEL: model,
        ANTHROPIC_DEFAULT_SONNET_MODEL_NAME: modelName,
        ANTHROPIC_DEFAULT_OPUS_MODEL: model,
        ANTHROPIC_DEFAULT_OPUS_MODEL_NAME: modelName,
        CLAUDE_CODE_AUTO_COMPACT_WINDOW: String(contextWindow),
        CLAUDE_AUTOCOMPACT_PCT_OVERRIDE: String(CLAUDE_CODE_AUTO_COMPACT_PERCENT),
        ENABLE_TOOL_SEARCH: 'true',
      },
    }
    env = settings.env
    await writeScopedFile('settings', `${JSON.stringify(settings, null, 2)}\n`)
    const globalMcpPath = getLiveConfigFileDefinition(tool.id, 'mcp')?.absolutePath
    const existingMcpPath = getScopedConfigFileDefinition(tool.id, 'mcp', scope)?.absolutePath
    const globalMcpConfig = globalMcpPath ? await safeReadFile(globalMcpPath) : ''
    const existingMcpConfig = existingMcpPath ? await safeReadFile(existingMcpPath) : ''
    await writeScopedFile('mcp', claudeMcpConfigJson(scope.profile, globalMcpConfig, existingMcpConfig))
    await writeScopedFile('prompt', hermesPromptDocument(scopedSystemPrompt))

    const settingsPath = join(rootDir, 'settings.json')
    const mcpPath = join(rootDir, 'mcp.json')
    const promptPath = join(rootDir, 'hermes-rules.md')
    args = [
      '--settings',
      settingsPath,
      ...(input.isolateSettings ? ['--setting-sources', 'local'] : []),
      '--mcp-config',
      mcpPath,
      '--append-system-prompt-file',
      promptPath,
      ...claudeCodePermissionArgs(),
    ]
  } else if (tool.id === 'codex') {
    if (apiMode !== 'chat_completions' && apiMode !== 'codex_responses' && apiMode !== 'anthropic_messages') {
      const err = new Error('Codex launch only supports OpenAI Chat Completions, OpenAI Responses, or Anthropic Messages providers')
      ;(err as any).status = 400
      throw err
    }
    const proxyTarget = baseUrl && apiKey
      ? registerCodexProxyTarget({
          profile: scope.profile,
          provider,
          model,
          baseUrl,
          apiKey,
          apiMode,
          reasoningEffort,
          agentId: tool.id,
          agentSessionId: isolatedInput.agentSessionId,
          chatSessionId: isolatedInput.sessionId,
        })
      : null
    const codexBaseUrl = proxyTarget?.baseUrl || baseUrl
    const codexApiKey = proxyTarget?.token || apiKey
    const providerId = 'custom'
    const catalogPath = join(rootDir, CODEX_MODEL_CATALOG_FILE)
    const toolSearchFeatures = await resolveCodexToolSearchConfig()
    const featureConfig = toolSearchFeatures.toolSearch || toolSearchFeatures.alwaysDefer
      ? [
          '',
          '[features]',
          ...(toolSearchFeatures.toolSearch ? ['tool_search = true'] : []),
          ...(toolSearchFeatures.alwaysDefer ? ['tool_search_always_defer_mcp_tools = true'] : []),
        ]
      : []
    const configToml = [
      `model_catalog_json = ${JSON.stringify(catalogPath)}`,
      `model_provider = ${JSON.stringify(providerId)}`,
      `model = ${JSON.stringify(model)}`,
      'model_reasoning_summary = "auto"',
      ...(reasoningEffort ? [`model_reasoning_effort = ${JSON.stringify(reasoningEffort)}`] : []),
      `developer_instructions = ${tomlMultilineString(scopedSystemPrompt.trim())}`,
      'disable_response_storage = true',
      '',
      `[model_providers.${providerId}]`,
      `name = ${JSON.stringify(provider)}`,
      ...(codexBaseUrl ? [`base_url = ${JSON.stringify(codexBaseUrl)}`] : []),
      'wire_api = "responses"',
      'requires_openai_auth = false',
      ...(codexApiKey ? [`experimental_bearer_token = ${JSON.stringify(codexApiKey)}`] : []),
      '',
      codexMcpConfigToml(
        scope.profile,
        await safeReadFile(getLiveConfigFileDefinition(tool.id, 'config')?.absolutePath || ''),
        await safeReadFile(getScopedConfigFileDefinition(tool.id, 'config', scope)?.absolutePath || ''),
      ),
      ...featureConfig,
    ].join('\n')
    const catalog = buildCodexModelCatalog({
      profile: scope.profile,
      provider,
      model,
      presetModels: Array.isArray(preset?.models) ? preset.models : [],
    })
    await writeFile(catalogPath, `${JSON.stringify(catalog, null, 2)}\n`, 'utf-8')
    files.push({ key: 'model_catalog', path: CODEX_MODEL_CATALOG_FILE, absolutePath: catalogPath })
    await writeScopedFile('config', configToml)
    await writeScopedFile('auth', `${JSON.stringify({}, null, 2)}\n`)

    env = { CODEX_HOME: rootDir }
    args = [
      '--model', model,
      ...(reasoningEffort ? ['-c', `model_reasoning_effort=${JSON.stringify(reasoningEffort)}`] : []),
    ]
  } else if (tool.id === 'dsh') {
    if (apiMode !== 'chat_completions' && apiMode !== 'codex_responses') {
      const err = new Error('DeepSeek Harness launch only supports OpenAI Chat Completions or OpenAI Responses providers')
      ;(err as any).status = 400
      throw err
    }
    const sdkLaunch = await resolveDshSdkLaunch()
    if (sdkLaunch) {
      // Full capability: JSON-RPC SDK runtime installed. Drive a Hermes-managed
      // cordis.yml composition over a persistent child process.
      const sessionRoot = join(rootDir, 'sessions')
      await mkdir(sessionRoot, { recursive: true })
      env = {
        DSH_CWD: workspaceDir,
        DSH_SESSION_ROOT: sessionRoot,
        DSH_SYSTEM_PROMPT: scopedSystemPrompt,
        DSH_MAX_TOKENS_AS_SUCCESS: 'true',
        // The cordis composition boots an llm-pi-ai route that requires a
        // non-empty base URL; default to the official DeepSeek endpoint when
        // the provider does not carry its own.
        ...(baseUrl ? { DEEPSEEK_BASE_URL: baseUrl } : { DEEPSEEK_BASE_URL: DEFAULT_DEEPSEEK_BASE_URL }),
        ...(apiKey ? { DEEPSEEK_API_KEY: apiKey } : {}),
      }
      if (sdkLaunch.args.length > 0) {
        // Local source checkout: the harness's own cordis.yml supplies the
        // composition, and the runtime process must run from the checkout root.
        command = sdkLaunch.command
        runtimeCwd = sdkLaunch.runtimeCwd
        args = sdkLaunch.args
      } else {
        command = sdkLaunch.command
        await writeScopedFile('cordis', buildDshSdkCordisTemplate(apiMode))
        args = [join(rootDir, DSH_SDK_CORDIS_FILE)]
      }
    } else {
      // Basic capability: headless CLI single-turn mode.
      const settingsYaml = apiMode === 'codex_responses'
        ? [
            'llm-pi-ai:',
            '  providers:',
            '    openai-responses:',
            '      apiKeyEnv: DEEPSEEK_API_KEY',
            '      api: openai-responses',
            ...(baseUrl ? [`      baseURL: ${yamlString(baseUrl)}`] : []),
            '      models:',
            `        - id: ${yamlString(model)}`,
            '          contextWindow: 262144',
            '          maxTokens: 32768',
            'agent-default-model:',
            '  provider: openai-responses',
            `  model: ${yamlString(model)}`,
          ].join('\n')
        : [
            'llm-deepseek:',
            ...(baseUrl ? [`  baseURL: ${yamlString(baseUrl)}`] : []),
            '  apiKeyEnv: DEEPSEEK_API_KEY',
            '  thinking: disabled',
            'agent-default-model:',
            '  provider: deepseek-official',
            `  model: ${yamlString(model)}`,
          ].join('\n')
      await writeScopedFile('settings', `${settingsYaml}\n`)

      env = {
        DSH_HOME: rootDir,
        ...(baseUrl ? { DEEPSEEK_BASE_URL: baseUrl } : {}),
        ...(apiKey ? { DEEPSEEK_API_KEY: apiKey } : {}),
      }
      args = ['--profile', 'headless']
    }
  } else {
    if (!existsSync(getPiMcpAdapterEntry())) {
      const err = new Error(`Pi MCP Adapter ${PI_MCP_ADAPTER_VERSION} is not installed. Reinstall Pi from Coding Agents.`)
      ;(err as any).status = 400
      throw err
    }
    // Keep a stable, credential-free Pi config set at the same level as the
    // Claude Code and Codex homes. Each conversation still gets an isolated
    // runs/<hash> directory containing its provider credentials and sessions.
    await ensurePiScopedBaseConfigFiles(scope)
    const proxyTarget = baseUrl && apiKey
      ? registerCodexProxyTarget({
          profile: scope.profile,
          provider,
          model,
          baseUrl,
          apiKey,
          apiMode,
          reasoningEffort,
          agentId: tool.id,
          agentSessionId: isolatedInput.agentSessionId,
          chatSessionId: isolatedInput.sessionId,
        })
      : null
    const piBaseUrl = proxyTarget?.baseUrl || baseUrl
    const piApiKey = proxyTarget?.token || apiKey
    const sessionsDir = join(rootDir, 'sessions')
    const dynamicPromptPath = join(rootDir, PI_DYNAMIC_PROMPT_FILE)
    const studioExtensionPath = join(rootDir, PI_STUDIO_EXTENSION_FILE)
    await mkdir(sessionsDir, { recursive: true })
    await writeRuntimeFile('studio_extension', PI_STUDIO_EXTENSION_FILE, piStudioRuntimeExtension())
    await writeRuntimeFile('dynamic_prompt', PI_DYNAMIC_PROMPT_FILE, '')
    await writeRuntimeFile('settings', 'settings.json', piSettingsConfig(
      (await safeReadFile(getScopedConfigFileDefinition(tool.id, 'settings', scope)?.absolutePath || '')) || '',
      studioExtensionPath,
    ))
    await writeRuntimeFile('models', 'models.json', piModelsConfig({
      baseUrl: piBaseUrl,
      apiKey: piApiKey,
      apiMode,
      model,
      profile: scope.profile,
      provider,
      reasoningEffort,
    }))
    if (proxyTarget) {
      const proxyTargetPath = join(rootDir, PI_PROXY_TARGET_FILE)
      await atomicWritePrivateFile(proxyTargetPath, await serializePiProxyTarget({
        profile: scope.profile,
        provider,
        model,
        baseUrl,
        apiMode,
        reasoningEffort,
        agentId: tool.id,
        agentSessionId: isolatedInput.agentSessionId,
        chatSessionId: isolatedInput.sessionId,
      }, apiKey, proxyTarget.token))
      files.push({ key: 'proxy_target', path: PI_PROXY_TARGET_FILE, absolutePath: proxyTargetPath })
    }
    await writeRuntimeFile('mcp', 'mcp.json', piMcpConfig(
      scope.profile,
      await safeReadFile(getLiveConfigFileDefinition(tool.id, 'mcp')?.absolutePath || ''),
      await safeReadFile(getScopedConfigFileDefinition(tool.id, 'mcp', scope)?.absolutePath || ''),
    ))
    await writeRuntimeFile('prompt', 'APPEND_SYSTEM.md', `${scopedSystemPrompt.trim()}\n`)
    env = {
      PI_CODING_AGENT_DIR: rootDir,
      PI_CODING_AGENT_SESSION_DIR: sessionsDir,
      HERMES_PI_DYNAMIC_PROMPT_FILE: dynamicPromptPath,
    }
    args = [
      ...(input.piOutputMode === 'rpc' ? ['--mode', 'rpc'] : []),
      '--provider', PI_PROVIDER_ID,
      '--model', model,
      ...(input.agentNativeSessionId ? ['--session-id', input.agentNativeSessionId] : []),
      '--session-dir', sessionsDir,
      '--append-system-prompt', join(rootDir, 'APPEND_SYSTEM.md'),
      ...(input.piOutputMode === 'rpc'
        ? [input.approveProjectConfig === true ? '--approve' : '--no-approve']
        : []),
    ]
  }

  let shellCommand = buildLaunchShellCommand({
    workspaceDir,
    env,
    command,
    args,
  })
  const launcherPath = await writeLauncherScript({
    rootDir,
    workspaceDir,
    env,
    command,
    args,
  })
  files.push({
    key: 'launcher',
    path: process.platform === 'win32' ? WINDOWS_LAUNCHER_FILE : POSIX_LAUNCHER_FILE,
    absolutePath: launcherPath,
  })
  shellCommand = buildLauncherShellCommand(workspaceDir, launcherPath)

  return {
    agentId: tool.id,
    mode,
    profile: scope.profile,
    provider,
    model,
    apiMode,
    rootDir,
    workspaceDir,
    command,
    args,
    env,
    shellCommand,
    files,
    reasoningEffort,
    ...(runtimeCwd ? { runtimeCwd } : {}),
  }
}

export async function startCodingAgentRun(
  id: string,
  input: CodingAgentLaunchInput & { sessionId: string },
  state?: SessionState,
): Promise<CodingAgentRunStartResult> {
  const sessionId = String(input.sessionId || '').trim()
  if (!sessionId) {
    const err = new Error('sessionId is required')
    ;(err as any).status = 400
    throw err
  }
  const existingSession = getSession(sessionId)
  const sessionSource = input.sessionSource === 'global_agent'
    ? 'global_agent'
    : input.sessionSource === 'group_chat'
      ? 'group_chat'
    : input.sessionSource === 'workflow'
      ? 'workflow'
      : 'coding_agent'
  const existingAgentSessionId = existingSession?.agent_session_id || ''
  const resolvedInput = await resolveStoredProviderLaunchInput(input, existingSession)
  const requestedMode = resolvedInput.mode === 'global' ? 'global' : 'scoped'
  const requestedProvider = String(resolvedInput.provider || '').trim().toLowerCase()
  assertScopedCodingAgentProviderAllowed(requestedMode, requestedProvider)
  if (requestedMode !== 'global' && (!String(resolvedInput.baseUrl || '').trim() || !String(resolvedInput.apiKey || '').trim())) {
    const err = new Error('Coding agent provider credentials are missing. Re-select the provider/model or update the provider API key before continuing this session.')
    ;(err as any).status = 400
    throw err
  }
  const agentSessionId = resolvedInput.agentSessionId || existingAgentSessionId || makeAgentSessionId()
  const canResumeNativeSession = existingSession
    ? storedCodingAgentMode(existingSession) === requestedMode &&
      (existingSession.agent === persistedAgentId(id) || !existingSession.agent) &&
      String(existingSession.provider || '').trim() === String(resolvedInput.provider || '').trim() &&
      String(existingSession.model || '').trim() === String(resolvedInput.model || '').trim() &&
      (!String(existingSession.api_mode || '').trim() || String(existingSession.api_mode || '').trim() === String(resolvedInput.apiMode || '').trim())
    : false
  const existingNativeSessionId = canResumeNativeSession ? existingSession?.agent_native_session_id || '' : ''
  const agentNativeSessionId = resolvedInput.agentNativeSessionId || existingNativeSessionId || (id === 'claude-code' || id === 'pi' ? randomUUID() : '')
  const launch = await prepareCodingAgentLaunch(id, {
    ...resolvedInput,
    sessionId,
    agentSessionId,
    agentNativeSessionId,
    isolateSettings: true,
    piOutputMode: id === 'pi' ? 'rpc' : undefined,
  })
  const commandExecutionEnv = process.platform === 'win32'
    ? {
        ...(await commandEnv()),
        ...launch.env,
      }
    : launch.env
  const runtimeCommand = process.platform === 'win32'
    ? await resolveCommandForExecution(launch.command, commandExecutionEnv)
    : launch.command
  const runtimeEnv = launch.agentId === 'pi' ? launch.env : commandExecutionEnv
  const persistedProvider = String(resolvedInput.provider || launch.provider || '').trim() || launch.provider
  const started = codingAgentRunManager.start({
    agentSessionId,
    agentId: launch.agentId,
    mode: launch.mode,
    profile: launch.profile,
    provider: persistedProvider,
    model: launch.model,
    apiMode: launch.apiMode,
    sessionId,
    agentNativeSessionId,
    nativeResume: Boolean(existingNativeSessionId),
    command: runtimeCommand,
    args: launch.args,
    shellCommand: launch.shellCommand,
    workspaceDir: launch.workspaceDir,
    runtimeCwd: launch.runtimeCwd,
    env: runtimeEnv,
    state,
    reasoningEffort: launch.reasoningEffort,
    sessionSource: sessionSource === 'global_agent' || sessionSource === 'workflow' || sessionSource === 'group_chat'
      ? sessionSource
      : undefined,
  })
  updateSession(sessionId, {
    source: sessionSource,
    agent: persistedAgentId(launch.agentId),
    agent_mode: launch.mode,
    agent_session_id: agentSessionId,
    agent_native_session_id: agentNativeSessionId,
    model: launch.model,
    provider: persistedProvider,
    api_mode: launch.apiMode || '',
    workspace: launch.workspaceDir,
  })
  return {
    ...launch,
    provider: persistedProvider,
    agentSessionId,
    sessionId,
    pid: started.pid,
  }
}

export async function compactStoredCodingAgentSession(
  sessionId: string,
  profile: string,
): Promise<{ compacted: boolean; beforeTokens?: number | null; afterTokens?: number | null }> {
  const session = getSession(sessionId)
  if (!session || (session.agent !== 'codex' && session.agent !== 'claude')) {
    throw new Error('Coding agent session not found or is not a Codex/Claude Code session')
  }
  if (session.agent !== 'codex') {
    throw new Error('Claude Code compact requires an active session run')
  }
  const nativeSessionId = String(session.agent_native_session_id || '').trim()
  if (!nativeSessionId) {
    throw new Error('Codex session has no native thread to compact')
  }
  const resolved = await resolveStoredProviderLaunchInput({
    sessionId,
    profile,
    mode: session.agent_mode === 'global' ? 'global' : 'scoped',
    workspace: session.workspace || undefined,
    agentNativeSessionId: session.agent_native_session_id || undefined,
    agentSessionId: session.agent_session_id || undefined,
  }, session)
  const launch = await prepareCodingAgentLaunch('codex', {
    ...resolved,
    sessionId,
    profile,
    isolateSettings: true,
  })
  const launchEnv = {
    ...process.env,
    ...launch.env,
  }
  const command = process.platform === 'win32'
    ? await resolveCommandForExecution(launch.command, launchEnv)
    : launch.command
  return compactCodexThread({
    command,
    env: launchEnv,
    workspaceDir: launch.workspaceDir,
  }, nativeSessionId)
}

export function sendCodingAgentRunInput(
  sessionId: string,
  input: string,
  systemPrompt?: string,
  images: CodingAgentImageInput[] = [],
  storageInput?: string,
): { runId: string; messageId?: number } {
  return codingAgentRunManager.send(sessionId, input, { systemPrompt, images, storageInput })
}

export function stopCodingAgentRun(sessionId: string): { stopped: boolean } {
  return { stopped: codingAgentRunManager.stop(sessionId) }
}

export function invalidateCodingAgentProviderRuntime(profileInput: string, providerInput: string): {
  invalidatedRuns: number
  deferredRuns: number
} {
  const profile = normalizeScopeSegment(profileInput, 'default', 'profile')
  const providerIdentity = normalizeProviderIdentity(providerInput)
  const result = codingAgentRunManager.invalidateMatching(launch => (
    launch.profile === profile && launch.provider === providerIdentity
  ))
  return { invalidatedRuns: result.invalidated, deferredRuns: result.deferred }
}

export async function openCodingAgentNativeTerminal(id: string, input: CodingAgentLaunchInput): Promise<CodingAgentNativeLaunchResult> {
  const launch = await prepareCodingAgentLaunch(id, input)
  const terminal = await openNativeTerminal(launch.shellCommand)
  return {
    ...launch,
    nativeTerminal: true,
    terminal,
  }
}
